import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Scissors, Play, Pause, Plus, Trash2, Download, Eye, FileText, Folder, Users, ChevronDown, ChevronUp, CheckCircle, AlertCircle, Loader2, ExternalLink } from 'lucide-react';

// Import utility functions
import { 
  formatTime, 
  formatTimeForFilename, 
  cleanFilename, 
  parseTime, 
  formatTimeInput, 
  validateTimeRange, 
  createPartObject 
} from '../../utils/partsUtils';

import { 
  SUPPORTED_LANGUAGES, 
  getLanguageInfo, 
  getUsedLanguages 
} from '../../utils/languageUtils';

import { 
  BACKUP_KEY, 
  GROUPS_BACKUP_KEY, 
  backupPartsAndGroups, 
  restorePartsAndGroups, 
  clearBackupsForProject, 
  forceBackupSync, 
  debugAllBackups 
} from '../../utils/backupUtils';

// Import Export Manager functions
import { 
  handleExportPart as exportPartService, 
  handleExportAllParts as exportAllPartsService, 
  handleExportGroup as exportGroupService 
} from './ExportManager';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.variant === 'primary' ? props.theme.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.text};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-height: 36px;

  &:hover {
    background: ${props => props.variant === 'primary' ? props.theme.primary + 'dd' : props.theme.surface};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CutterSection = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.surface};
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0 0 16px 0;
`;

const TimeInputs = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
`;

const TimeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TimeLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.textSecondary};
`;

const TimeInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-size: 14px;
  width: 100px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const CurrentTimeButton = styled.button`
  padding: 6px 10px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: transparent;
  color: ${props => props.theme.text};
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.surface};
  }
`;

const PartNameInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const CreatePartForm = styled.div`
  display: flex;
  gap: 12px;
  align-items: end;
`;

const PartsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const PartsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PartCard = styled.div`
  padding: 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.surface};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.primary}50;
  }
`;

const PartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const PartName = styled.h5`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0;
`;

const PartActions = styled.div`
  display: flex;
  gap: 4px;
`;

const PartActionButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.border};
    color: ${props => props.theme.text};
  }

  &.danger:hover {
    background: ${props => props.theme.danger};
    color: white;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const PartInfo = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`;

const PartDuration = styled.span`
  font-weight: 500;
`;

const PartTimeRange = styled.span``;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
  color: ${props => props.theme.textSecondary};
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.theme.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const EmptyTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  margin: 0;
  max-width: 300px;
`;

const GroupSelectorModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const GroupSelectorContent = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  padding: 20px;
  max-width: 400px;
  width: 90%;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.border};
`;

const GroupSelectorTitle = styled.h3`
  margin: 0 0 16px 0;
  color: ${props => props.theme.text};
  font-size: 16px;
`;

const GroupOption = styled.div`
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.theme.background};
  
  &:hover {
    background: ${props => props.theme.primary}20;
    border-color: ${props => props.theme.primary};
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const GroupOptionTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GroupOptionInfo = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`;

const NewGroupOption = styled(GroupOption)`
  border-style: dashed;
  border-color: ${props => props.theme.primary};
  background: ${props => props.theme.primary}10;
  text-align: center;
  
  &:hover {
    background: ${props => props.theme.primary}20;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  justify-content: flex-end;
`;

const LanguageSelectorModal = styled(GroupSelectorModal)``;
const LanguageSelectorContent = styled(GroupSelectorContent)``;
const LanguageSelectorTitle = styled(GroupSelectorTitle)``;

const FormatSelectorModal = styled(GroupSelectorModal)``;
const FormatSelectorContent = styled(GroupSelectorContent)`
  max-width: 500px;
  max-height: 600px;
`;
const FormatSelectorTitle = styled(GroupSelectorTitle)``;

const WatermarkSelectorModal = styled(GroupSelectorModal)``;
const WatermarkSelectorContent = styled(GroupSelectorContent)`
  max-width: 500px;
  max-height: 600px;
`;
const WatermarkSelectorTitle = styled(GroupSelectorTitle)``;

const WatermarkOption = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.theme.surface};

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary}10;
  }
`;

const WatermarkIcon = styled.div`
  width: 40px;
  height: 40px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 4px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 10px;
  font-weight: 600;
  position: relative;
`;

const WatermarkDetails = styled.div`
  flex: 1;
`;

const WatermarkName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 4px;
`;

const WatermarkDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`;

// Subtitle Selector Styled Components
const SubtitleSelectorModal = styled(GroupSelectorModal)``;
const SubtitleSelectorContent = styled(GroupSelectorContent)`
  max-width: 500px;
  max-height: 600px;
`;
const SubtitleSelectorTitle = styled(GroupSelectorTitle)``;

const SubtitlePresetOption = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.theme.surface};

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary}10;
  }
`;

const SubtitlePresetIcon = styled.div`
  width: 40px;
  height: 40px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 4px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  font-size: 16px;
  font-weight: 600;
  position: relative;
`;

const SubtitlePresetDetails = styled.div`
  flex: 1;
`;

const SubtitlePresetName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 4px;
`;

const SubtitlePresetDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`;

const StatusModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const StatusModalContent = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 16px;
  padding: 32px;
  width: 90%;
  max-width: 450px;
  border: 1px solid ${props => props.theme.border};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: slideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @keyframes slideUp {
    from { transform: translateY(40px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const StatusIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  &.loading {
    background: ${props => props.theme.primary}20;
    color: ${props => props.theme.primary};
  }
  
  &.success {
    background: #28a74520;
    color: #28a745;
  }
  
  &.error {
    background: #dc354520;
    color: #dc3545;
  }
`;

const StatusTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.theme.text};
  margin: 0 0 12px 0;
`;

const StatusMessage = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: ${props => props.theme.border};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${props => props.theme.primary} 0%, #764ba2 100%);
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const StatusActions = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const StatusButton = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &.primary {
    background: ${props => props.theme.primary};
    color: white;
    border: none;
    &:hover { background: ${props => props.theme.primary}dd; }
  }

  &.secondary {
    background: ${props => props.theme.surface};
    color: ${props => props.theme.text};
    border: 1px solid ${props => props.theme.border};
    &:hover { background: ${props => props.theme.background}; }
  }
`;

const FormatOption = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.theme.surface};

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary}10;
  }
`;

const FormatIcon = styled.div`
  width: 40px;
  height: 40px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 4px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 10px;
  font-weight: 600;
  position: relative;
  
  ${props => props.ratio && `
    aspect-ratio: ${props.ratio};
  `}
`;

const FormatDetails = styled.div`
  flex: 1;
`;

const FormatName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 4px;
`;

const FormatDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`;
const LanguageOption = styled(GroupOption)``;
const LanguageOptionTitle = styled(GroupOptionTitle)``;
const LanguageOptionInfo = styled(GroupOptionInfo)``;

const LanguageBadge = styled.span`
  font-size: 9px;
  padding: 2px 6px;
  background: ${props => props.color || '#007bff20'};
  border-radius: 8px;
  color: ${props => props.textColor || '#007bff'};
  font-weight: 600;
  margin-left: 6px;
  border: 1px solid ${props => props.borderColor || '#007bff40'};
`;

const CollapsibleGroupTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.primary};
  }
`;

const GroupTitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExpandIcon = styled.div`
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const GroupContent = styled.div`
  margin-top: 16px;
  transition: all 0.3s ease;
  overflow: hidden;
`;

const DataRecoveryIndicator = styled.div`
  padding: 8px 12px;
  margin-bottom: 16px;
  border-radius: 6px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.type === 'warning' ? '#fff3cd' : '#d1ecf1'};
  border: 1px solid ${props => props.type === 'warning' ? '#ffeaa7' : '#bee5eb'};
  color: ${props => props.type === 'warning' ? '#856404' : '#0c5460'};
`;

// Note: Backup utilities moved to utils/backupUtils.js

// Note: Language constants moved to utils/languageUtils.js

// Note: Language functions moved to utils/languageUtils.js

// Note: All backup functions moved to utils/backupUtils.js



const PartsCutter = ({ project, onUpdate, currentTime, duration, onSeek, isPlaying, onPlayPause }) => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [partName, setPartName] = useState('');
  const [currentPlayingPart, setCurrentPlayingPart] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Initialize all groups as expanded by default
    const initialState = {
      'individual-parts': true // Individual parts group expanded by default
    };
    (project.groups || []).forEach(group => {
      initialState[group.id] = true;
    });
    return initialState;
  });
  const [backupStatus, setBackupStatus] = useState(null);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [moveAction, setMoveAction] = useState(null); // { type: 'part'|'segment', data: part|segment, from?: groupId }
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [languageAction, setLanguageAction] = useState(null);
  const [showFormatSelector, setShowFormatSelector] = useState(false);
  const [formatAction, setFormatAction] = useState(null); // { type: 'part'|'segment'|'group', data: part/segment/group }
  const [selectedMultiFormats, setSelectedMultiFormats] = useState([]);

  // Watermark selector states
  const [showWatermarkSelector, setShowWatermarkSelector] = useState(false);
  const [watermarkAction, setWatermarkAction] = useState(null); // { type: 'part'|'segment'|'group', data: part/segment/group }
  const [selectedMultiWatermarks, setSelectedMultiWatermarks] = useState([]);
  
  // 🟢 NEW: Export Status State
  const [exportStatus, setExportStatus] = useState(null); // { type, message, progress, folder, path }

  const [individualPartsLanguage, setIndividualPartsLanguage] = useState(null);
  
  // 🔧 CRITICAL FIX: Restore language from localStorage on component mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('individualPartsLanguage');
    if (savedLanguage && savedLanguage !== 'null' && savedLanguage !== 'undefined') {
      console.log('🔄 Restoring language from localStorage:', savedLanguage);
      setIndividualPartsLanguage(savedLanguage);
    }
  }, []); // Run only once on mount
  
  // 🔧 CLEANUP: Remove individualPartsLanguage from localStorage to prevent group language conflicts
  useEffect(() => {
    console.log('🧹 Cleaning up individualPartsLanguage from localStorage to prevent group conflicts');
    localStorage.removeItem('individualPartsLanguage');
    localStorage.removeItem('currentUILanguage');
    localStorage.removeItem('lastExportLanguage');
  }, []);
  
  // 🔧 DISABLED: Don't sync project language to localStorage to avoid overriding group languages
  // useEffect(() => {
  //   const projectLanguage = project.settings?.subtitleLanguage || project.settings?.language;
  //   const currentStored = localStorage.getItem('individualPartsLanguage');
  //   
  //   // Only sync if localStorage is empty, null, or 'original' - preserve explicit language selections
  //   if (projectLanguage && (!currentStored || currentStored === 'null' || currentStored === 'original')) {
  //     console.log('🔄 Syncing project language to localStorage (individual parts only):', projectLanguage);
  //     localStorage.setItem('individualPartsLanguage', projectLanguage);
  //     setIndividualPartsLanguage(projectLanguage);
  //   }
  // }, [project.settings?.subtitleLanguage, project.settings?.language]);
  
  // Ref to prevent multiple auto-restores
  const hasAutoRestored = useRef(false);
  
  // Reset auto-restore flag when project changes
  useEffect(() => {
    hasAutoRestored.current = false;
  }, [project.id]);

  // Debug logging
    console.log('PartsCutter received props:', {
    hasOnSeek: !!onSeek,
    hasOnPlayPause: !!onPlayPause,
    isPlaying,
    currentTime, 
    duration,
    partsCount: project.parts?.length || 0,
    groupsCount: project.groups?.length || 0,
    hasAutoRestored: hasAutoRestored.current,
    groupsWithSubtitleSettings: project.groups?.filter(g => g.subtitleSettings)?.length || 0
  });
  
  // 🔍 CRITICAL DEBUG: Log current groups subtitle status
  if (project.groups && project.groups.length > 0) {
    const groupsStatus = project.groups.map(g => ({
      id: g.id,
      name: g.name,
      hasSubtitleSettings: !!g.subtitleSettings,
      subtitleSettings: g.subtitleSettings,
      subtitlePresetNames: g.subtitlePresetNames,
      fontColor: g.subtitleSettings?.fontColor
    }));
    console.log('🔍 CURRENT GROUPS SUBTITLE STATUS:', groupsStatus);
    console.log('🔍 GROUPS WITH SUBTITLE SETTINGS:', groupsStatus.filter(g => g.hasSubtitleSettings).length);
    
    // Log each group individually for clarity
    groupsStatus.forEach(g => {
      console.log(`🔍 Group "${g.name}": hasSubtitleSettings=${g.hasSubtitleSettings}, fontColor=${g.fontColor}, presetNames=${g.subtitlePresetNames}`);
    });
  }

  const parts = project.parts || [];
  const groups = project.groups || [];

  // Auto-save and backup data whenever parts or groups change (with debounce)
  useEffect(() => {
    if (project?.id && (parts.length > 0 || groups.length > 0)) {
      // Only backup when there's actual data and debounce the backup
      const saveTimeout = setTimeout(() => {
        try {
          backupPartsAndGroups(project.id, parts, groups, individualPartsLanguage);
          console.log('💾 Auto-saved changes to backup', {
            hasData: true,
            partsCount: parts.length,
            groupsCount: groups.length
          });
        } catch (error) {
          console.error('💾 Auto-save backup failed:', error);
        }
      }, 1000);
      
      return () => clearTimeout(saveTimeout);
    }
  }, [project.id, parts, groups, individualPartsLanguage]);

  // 🟢 NEW: Handle Opening Folder
  const handleOpenFolder = async (folderPath) => {
    if (!folderPath) return;
    try {
      await fetch('http://localhost:8001/open-folder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: folderPath })
      });
    } catch (error) {
      console.error('Failed to open folder:', error);
    }
  };

  const handleExportGroupClick = async (group) => {
    setExportStatus({ type: 'loading', message: `Preparing to export group "${group.name}"...`, progress: 0 });
    try {
      await exportGroupService(group, project, transcript, onUpdate, (status) => {
        setExportStatus(prev => ({ ...prev, ...status }));
      });
    } catch (error) {
      setExportStatus({ type: 'error', message: error.message || 'Export failed' });
    }
  };

  const handleExportAllPartsClick = async (partsToExport) => {
    if (!partsToExport || partsToExport.length === 0) return;
    setExportStatus({ type: 'loading', message: `Preparing to export ${partsToExport.length} parts...`, progress: 0 });
    try {
      await exportAllPartsService(partsToExport, project, transcript, onUpdate, (status) => {
        setExportStatus(prev => ({ ...prev, ...status }));
      });
    } catch (error) {
      setExportStatus({ type: 'error', message: error.message || 'Export failed' });
    }
  };

  const handleExportPartClick = async (part) => {
    setExportStatus({ type: 'loading', message: `Preparing to export "${part.name || 'part'}"...`, progress: 0 });
    try {
      await exportPartService(part, project, transcript, null, null, onUpdate, (status) => {
        setExportStatus(prev => ({ ...prev, ...status }));
      });
    } catch (error) {
      setExportStatus({ type: 'error', message: error.message || 'Export failed' });
    }
  };

  // Auto-restore backup if current data is empty but backup exists (only run once per project)
  useEffect(() => {
    console.log('🔄 Auto-restore useEffect triggered:', {
      projectId: project.id,
      hasAutoRestored: hasAutoRestored.current,
      partsLength: parts.length,
      groupsLength: groups.length,
      groupsWithSubtitles: groups.filter(g => g.subtitleSettings).length
    });
    
    // 🔒 DISABLE AUTO-RESTORE COMPLETELY - It's causing issues with subtitle presets
    // Mark as restored immediately to prevent any backup restoration
    if (project.id && !hasAutoRestored.current) {
      console.log('🔒 DISABLING auto-restore to prevent subtitle preset conflicts');
      hasAutoRestored.current = true;
      return;
    }
    
    // OLD LOGIC (DISABLED):
    // Only run once per project AND only if we don't have any current data
    // if (project.id && !hasAutoRestored.current && parts.length === 0 && groups.length === 0) {
    //   const restored = restorePartsAndGroups(project.id);
    //   
    //   // Auto-restore if backup has real data
    //   const hasRealRestoredData = (restored.parts.length > 0) || (restored.groups.length > 0) || restored.individualPartsLanguage;
    //   
    //   if (hasRealRestoredData) {
    //     console.log('🔄 Auto-restoring from backup...', {
    //       partsFound: restored.parts.length,
    //       groupsFound: restored.groups.length,
    //       hasIndividualLang: !!restored.individualPartsLanguage
    //     });
    //     
    //     hasAutoRestored.current = true; // Mark as restored to prevent loops
    //     
    //     // Auto-restore the backup data
    //     onUpdate({ 
    //       parts: restored.parts, 
    //       groups: restored.groups 
    //     });
    //     
    //     // Restore individual parts language
    //     if (restored.individualPartsLanguage) {
    //       setIndividualPartsLanguage(restored.individualPartsLanguage);
    //     }
    //     
    //     setBackupStatus({
    //       type: 'info',
    //       message: `✅ Restored ${restored.parts.length} parts and ${restored.groups.length} groups from backup`,
    //       action: null
    //     });
    //     
    //     // Hide message after 5 seconds
    //     setTimeout(() => {
    //       setBackupStatus(null);
    //     }, 5000);
    //   } else {
    //     // Mark as restored even if no backup data to prevent constant checking
    //     hasAutoRestored.current = true;
    //   }
    // }
  }, [project.id, onUpdate]); // Only depend on project.id to run once per project

  // Load subtitle settings from preset names on project initialization
  useEffect(() => {
    console.log('🎯 Checking for missing subtitle settings in groups...');
    
    const getSubtitleSettingsFromPreset = (presetId) => {
      try {
        const savedPresets = JSON.parse(localStorage.getItem('subtitlePresets') || '[]');
        const preset = savedPresets.find(p => p.id === presetId);
        
        if (preset) {
          let subtitleSettings;
          
          if (preset.settings) {
            // Old format: settings are nested in a settings object
            subtitleSettings = {
              enabled: preset.settings.enabled !== undefined ? preset.settings.enabled : true,
              maxWordsPerLine: preset.settings.maxWordsPerLine !== undefined ? preset.settings.maxWordsPerLine : 8,
              fontSize: preset.settings.fontSize !== undefined ? preset.settings.fontSize : 36,
              fontColor: preset.settings.fontColor || '#ffff00',
              backgroundColor: preset.settings.backgroundColor || '#000000',
              backgroundOpacity: preset.settings.backgroundOpacity !== undefined ? preset.settings.backgroundOpacity : 90,
              position: preset.settings.position || 'bottom-center'
            };
          } else {
            // New format: settings are stored directly in preset object (WITH ADVANCED FEATURES)
            subtitleSettings = {
              enabled: preset.enabled !== undefined ? preset.enabled : true,
              maxWordsPerLine: preset.maxWordsPerLine !== undefined ? preset.maxWordsPerLine : 8,
              fontSize: preset.fontSize !== undefined ? preset.fontSize : 36,
              fontColor: preset.fontColor || '#ffff00',
              backgroundColor: preset.backgroundColor || '#000000',
              backgroundOpacity: preset.backgroundOpacity !== undefined ? preset.backgroundOpacity : 90,
              position: preset.position || 'bottom-center',
              // 🆕 ADVANCED FEATURES FROM PRESET
              customPosition: preset.customPosition || {
                enabled: false,
                x: 50,
                y: 90
              },
              fontFamily: preset.fontFamily || 'Arial',
              customFont: preset.customFont || null,
              textShadow: preset.textShadow || {
                enabled: true,
                color: '#000000',
                blur: 2,
                offsetX: 1,
                offsetY: 1
              }
            };
          }
          
          console.log(`🎯 Loaded subtitle settings WITH ADVANCED FEATURES for preset "${preset.name}" (${presetId}):`, subtitleSettings);
          return subtitleSettings;
        } else {
          console.warn(`🎯 Subtitle preset not found: ${presetId}`);
          return null;
        }
      } catch (error) {
        console.error('🎯 Error loading subtitle preset settings:', error);
        return null;
      }
    };

    // Check groups for missing subtitle settings
    let groupsUpdated = false;
    const updatedGroups = groups.map(group => {
      // If group has preset names but no subtitle settings, load them
      if (group.subtitlePresetNames && group.subtitlePresetNames.length > 0 && !group.subtitleSettings) {
        console.log(`🎯 Group "${group.name}" has preset names but no settings:`, group.subtitlePresetNames);
        
        // Use the first preset name to load settings
        const firstPresetName = group.subtitlePresetNames[0];
        const subtitleSettings = getSubtitleSettingsFromPreset(firstPresetName);
        
        if (subtitleSettings) {
          console.log(`🎯 Loading subtitle settings for group "${group.name}" from preset "${firstPresetName}"`);
          groupsUpdated = true;
          return {
            ...group,
            subtitleSettings: subtitleSettings
          };
        }
      }
      
      return group;
    });

    // Update project if any groups were updated
    if (groupsUpdated) {
      console.log('🎯 Updating groups with loaded subtitle settings');
      onUpdate({ groups: updatedGroups });
    }
  }, [groups, onUpdate]); // Run when groups change

  // Combine all individual parts (manual, transcript, etc.) except those in groups
  const allIndividualParts = parts || [];
  
  // Get parts that are in groups to filter them out from individual parts display
  const partsInGroups = new Set();
  groups.forEach(group => {
    if (group.parts) {
      group.parts.forEach(part => partsInGroups.add(part.id));
    }
  });
  
  // Individual parts are those not in any group
  const individualParts = allIndividualParts.filter(part => !partsInGroups.has(part.id));

  // Get transcript for group display
  const transcript = Array.isArray(project.transcript) ? project.transcript : [];

  // Auto-pause when reaching end of current playing part
  useEffect(() => {
    if (currentPlayingPart && isPlaying && currentTime >= currentPlayingPart.endTime) {
      console.log('🛑 Auto-pausing at end of part:', currentPlayingPart.name || 'segment');
      onPlayPause(false);
      setCurrentPlayingPart(null);
    }
  }, [currentTime, currentPlayingPart, isPlaying, onPlayPause]);

  // Note: Utility functions (formatTime, parseTime, etc.) are now imported from utils/partsUtils.js

  const handleSetCurrentTime = (type) => {
    const formattedTime = formatTimeInput(currentTime);
    if (type === 'start') {
      setStartTime(formattedTime);
    } else {
      setEndTime(formattedTime);
    }
  };

  const handleCreatePart = () => {
    if (!startTime || !endTime || !partName) {
      alert('Please fill in all fields');
      return;
    }

    // Use imported validation function
    const validation = validateTimeRange(startTime, endTime, duration);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    // Use imported part creation function
    const newPart = createPartObject(partName, startTime, endTime);
    const updatedParts = [...parts, newPart];
    onUpdate({ parts: updatedParts });
    
    // Force backup immediately after creating a part
    setTimeout(() => {
      backupPartsAndGroups(project.id, updatedParts, groups, individualPartsLanguage);
    }, 100);

    // Reset form
    setStartTime('');
    setEndTime('');
    setPartName('');
  };

  const handleDeletePart = (partId) => {
    if (window.confirm('Are you sure you want to delete this part?')) {
      console.log('🗑️ Deleting individual part:', { partId, currentPartsCount: parts.length });
      
      const updatedParts = parts.filter(part => part.id !== partId);
      
      try {
      onUpdate({ parts: updatedParts });
        console.log('✅ Successfully deleted individual part');
        
        // Force backup after deletion - with validation
        setTimeout(() => {
          if (project?.id) {
            backupPartsAndGroups(project.id, updatedParts, groups, individualPartsLanguage);
            console.log('💾 Backed up after part deletion');
          } else {
            console.warn('💾 Skip backup - invalid project ID');
          }
        }, 200);
      } catch (error) {
        console.error('❌ Failed to delete part:', error);
        console.error('❌ Error details:', {
          message: error.message,
          partId,
          remainingParts: updatedParts.length
        });
        alert('❌ Save failed after deleting part. Please try again or check console for details.');
      }
    }
  };

  const handlePreviewPart = (part) => {
    // Seek video player to part start time (TODO: connect to video ref)
    console.log('🎬 Previewing part:', part.name, 'at', formatTime(part.startTime));
  };

  const handleExportPart = async (part) => {
    return exportPartService(part, project, transcript, null, null, onUpdate);
  };

  const handleExportAllParts = async () => {
    return exportAllPartsService(parts, project, transcript, onUpdate);
  };

  const handleExportGroup = async (group) => {
    return exportGroupService(group, project, transcript, onUpdate);
  };

  const handleExportSegment = async (segment) => {
    return exportPartService(segment, project, transcript, null, null, onUpdate);
  };


  const handleDeleteGroup = (groupId) => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      console.log('🗑️ Deleting group:', { groupId, currentGroupsCount: groups.length });
      
      const updatedGroups = groups.filter(group => group.id !== groupId);
      
      try {
        onUpdate({ groups: updatedGroups });
        console.log('✅ Successfully deleted group');
        
        // Force backup immediately after deleting a group - with validation
        setTimeout(() => {
          if (project?.id) {
            backupPartsAndGroups(project.id, parts, updatedGroups, individualPartsLanguage);
            console.log('💾 Backed up after group deletion');
          } else {
            console.warn('💾 Skip backup - invalid project ID');
          }
        }, 200);
      } catch (error) {
        console.error('❌ Failed to delete group:', error);
        console.error('❌ Error details:', {
          message: error.message,
          groupId,
          remainingGroups: updatedGroups.length
        });
        alert('❌ Save failed after deleting group. Please try again or check console for details.');
      }
    }
  };

  // Split multi-segment parts into individual segments
  const handleSplitPart = (part) => {
    console.log('🔪 Splitting part into segments:', part);
    
    if (!part.isMultiSegmentSelection && !part.originalSegments) {
      alert('This part cannot be split - it\'s not a multi-segment selection.');
      return;
    }
    
    if (window.confirm(`Split "${part.text?.substring(0, 50)}..." into ${part.originalSegments?.length || 'individual'} segments?`)) {
      // Remove the original part
      const updatedParts = parts.filter(p => p.id !== part.id);
      
      // Add individual segments as separate parts
      const newParts = [];
      if (part.originalSegments) {
        part.originalSegments.forEach((segment, index) => {
          const newPart = {
            id: `${part.id}_split_${index}`,
            text: segment.text,
            startTime: segment.startTime,
            endTime: segment.endTime,
            speaker: segment.speaker,
            source: 'transcript-split',
            originalPartId: part.id,
            createdAt: new Date().toISOString()
          };
          newParts.push(newPart);
        });
      }
      
      onUpdate({ parts: [...updatedParts, ...newParts] });
      
      alert(`✅ Split complete!\n\nCreated ${newParts.length} individual segments from "${part.text?.substring(0, 30)}..."`);
    }
  };

  // Delete all individual parts (not in groups)
  const handleDeleteAllIndividualParts = () => {
    if (individualParts.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete all ${individualParts.length} individual parts?`)) {
      console.log('🗑️ Deleting all individual parts:', { count: individualParts.length });
      
      // Keep only parts that are in groups
      const updatedParts = parts.filter(part => partsInGroups.has(part.id));
      
      try {
        onUpdate({ parts: updatedParts });
        console.log(`✅ Successfully deleted ${individualParts.length} individual parts`);
        
        // Force backup after deletion - with validation
        setTimeout(() => {
          if (project?.id) {
            backupPartsAndGroups(project.id, updatedParts, groups, individualPartsLanguage);
            console.log('💾 Backed up after deleting all individual parts');
          } else {
            console.warn('💾 Skip backup - invalid project ID');
          }
        }, 200);
      } catch (error) {
        console.error('❌ Failed to delete all individual parts:', error);
        console.error('❌ Error details:', {
          message: error.message,
          deletedCount: individualParts.length,
          remainingParts: updatedParts.length
        });
        alert('❌ Save failed after deleting all parts. Please try again or check console for details.');
      }
    }
  };

  // Move segment between groups (drag and drop functionality)
  const handleMoveSegmentToGroup = (segmentId, fromGroupId, toGroupId) => {
    console.log('🔄 Moving segment between groups:', { segmentId, fromGroupId, toGroupId });
    
    if (fromGroupId === toGroupId) return;
    
    const updatedGroups = groups.map(group => {
      if (group.id === fromGroupId) {
        // Remove from source group
        return {
          ...group,
          segments: group.segments.filter(id => id !== segmentId),
          updatedAt: new Date().toISOString()
        };
      }
      if (group.id === toGroupId) {
        // Add to target group (avoid duplicates)
        const segments = group.segments || [];
        if (!segments.includes(segmentId)) {
          return {
            ...group,
            segments: [...segments, segmentId],
            updatedAt: new Date().toISOString()
          };
        }
      }
      return group;
    });
    // DON'T filter out empty groups - keep them
    
    try {
      onUpdate({ groups: updatedGroups });
      console.log('✅ Successfully moved segment between groups');
      
      const fromGroup = groups.find(g => g.id === fromGroupId)?.name;
      const toGroup = groups.find(g => g.id === toGroupId)?.name;
      alert(`✅ Successfully moved!\n\n📂 From: "${fromGroup}"\n📁 To: "${toGroup}"`);
    } catch (error) {
      console.error('❌ Failed to move segment between groups:', error);
      alert('❌ Save failed after moving segment. Changes may not be preserved.');
    }
  };

  // Add part to Individual Parts
  const handleAddToIndividualParts = (part) => {
    // Create a new individual part
    const newIndividualPart = {
      id: Date.now().toString(),
      name: part.text?.substring(0, 50) || 'Individual Part',
      startTime: part.startTime,
      endTime: part.endTime,
      duration: part.endTime - part.startTime,
      text: part.text,
      speaker: part.speaker,
      source: 'individual',
      originalPartId: part.id,
      createdAt: new Date().toISOString()
    };
    
    const updatedParts = [...parts, newIndividualPart];
    onUpdate({ parts: updatedParts });
    
    alert(`✅ Added to Individual Parts!\n\nPart: "${newIndividualPart.name}"\nDuration: ${formatTime(newIndividualPart.duration)}`);
  };

  // Add part to existing group
  const handleAddPartToGroup = (part, groupId) => {
    console.log('📁 Adding part to group:', { partId: part.id, groupId });
    
    const updatedGroups = groups.map(group => {
      if (group.id === groupId) {
        // Check if part is already in group
        const isPartAlreadyInGroup = group.parts?.some(p => p.id === part.id);
        if (isPartAlreadyInGroup) {
          alert('This part is already in the group!');
          return group;
        }
        
        return {
          ...group,
          parts: [...(group.parts || []), part],
          updatedAt: new Date().toISOString()
        };
      }
      return group;
    });
    
    onUpdate({ groups: updatedGroups });
    
    const groupName = groups.find(g => g.id === groupId)?.name;
    alert(`✅ Part added to "${groupName}" successfully!\n\nPart: "${part.text?.substring(0, 50)}..."\nDuration: ${formatTime(part.endTime - part.startTime)}`);
  };

  // Show group selector for moving parts/segments
  const showMoveToGroupSelector = (type, data, fromGroupId = null) => {
    setMoveAction({ type, data, from: fromGroupId });
    setShowGroupSelector(true);
  };

  // Show language selector for translation
  const openLanguageSelector = (type, data) => {
    setLanguageAction({ type, data });
    setShowLanguageSelector(true);
  };

  const openWatermarkSelector = (type, data) => {
    setWatermarkAction({ type, data });
    
    // Get current watermarks for this item
    let currentWatermarks = [];
    let currentWatermarkNames = [];
    
    if (type === 'group') {
      const groupData = data;
      if (groupData.id === 'individual-parts') {
        // Check individual parts for watermarks
        const watermarks = new Set();
        const watermarkNames = new Set();
        parts.forEach(part => {
          if (part.watermarks && part.watermarks.length > 0) {
            part.watermarks.forEach(w => watermarks.add(w));
          } else if (part.watermark) {
            watermarks.add(part.watermark);
          }
          if (part.watermarkNames && part.watermarkNames.length > 0) {
            part.watermarkNames.forEach(name => watermarkNames.add(name));
          }
        });
        currentWatermarks = Array.from(watermarks);
        currentWatermarkNames = Array.from(watermarkNames);
      } else {
        // Get group watermarks
        currentWatermarks = groupData.watermarks || (groupData.watermark ? [groupData.watermark] : []);
        currentWatermarkNames = groupData.watermarkNames || [];
      }
    }
    
    // Load saved watermark presets to check if current selection is from a preset
    const savedWatermarkPresets = (() => {
      try {
        return JSON.parse(localStorage.getItem('watermarkPresets') || '[]');
      } catch {
        return [];
      }
    })();
    
    // Initialize selected watermarks
    let initialSelected = [];
    
    if (currentWatermarks.length > 0 || currentWatermarkNames.length > 0) {
      // Find all matching presets by ID or name and select them
      const matchingPresets = savedWatermarkPresets.filter(preset => 
        currentWatermarks.includes(preset.id) || 
        currentWatermarkNames.includes(preset.id) || 
        currentWatermarkNames.includes(preset.name)
      );
      
      initialSelected = matchingPresets.map(preset => ({
        id: preset.id,
        name: preset.name,
        settings: preset.settings,
        isPreset: true,
        originalWatermark: preset.id
      }));
      
      console.log('🔄 Found matching watermark presets, selecting:', {
        currentWatermarkNames,
        matchingPresets: matchingPresets.map(p => p.name),
        initialSelected: initialSelected.map(p => p.name)
      });
    } else {
      // No need for fallback - we only use saved presets now
      initialSelected = [];
    }
    
    console.log('🔍 WATERMARK DEBUG: Setting initial selection:', initialSelected);
    setSelectedMultiWatermarks(initialSelected);
    setShowWatermarkSelector(true);
    
    // 🔧 FIX: Add a small delay to ensure state is updated
    setTimeout(() => {
      console.log('🔍 WATERMARK DEBUG: Current selection after delay:', selectedMultiWatermarks);
    }, 100);
  };

  const openFormatSelector = (type, data) => {
    setFormatAction({ type, data });
    
    // Get current preset names if available
    const currentPresetNames = (() => {
      if (type === 'group') {
        if (data.id === 'individual-parts') {
          // Get preset names from individual parts
          const allPresetNames = new Set();
          parts.forEach(part => {
            if (part.presetNames && Array.isArray(part.presetNames)) {
              part.presetNames.forEach(name => allPresetNames.add(name));
            } else if (part.presetName) {
              allPresetNames.add(part.presetName);
            }
          });
          return Array.from(allPresetNames);
        } else {
          // Get preset names from group
          if (data.presetNames && Array.isArray(data.presetNames)) {
            return data.presetNames;
          } else if (data.presetName) {
            return [data.presetName];
          }
          return [];
        }
      }
      return [];
    })();
    
    // Initialize with current formats
    const currentFormats = (() => {
      if (type === 'group') {
        if (data.id === 'individual-parts') {
          // Get formats from individual parts
          const formats = new Set();
          parts.forEach(part => {
            if (part.formats && part.formats.length > 0) {
              part.formats.forEach(f => formats.add(f));
            } else if (part.format) {
              formats.add(part.format);
            }
          });
          return Array.from(formats);
        } else {
          // Get formats from group
          return data.formats || (data.format ? [data.format] : []);
        }
      }
      return [];
    })();
    
    // Load saved presets to check if current selection is from a preset
    const savedPresets = (() => {
      try {
        return JSON.parse(localStorage.getItem('formatPresets') || '[]');
      } catch {
        return [];
      }
    })();
    
    // Initialize selected formats
    let initialSelected = [];
    
    if (currentPresetNames.length > 0) {
      // Find all matching presets by name and select them
      const matchingPresets = savedPresets.filter(preset => 
        currentPresetNames.includes(preset.name)
      );
      
      initialSelected = matchingPresets.map(preset => ({
        id: preset.id,
        name: preset.name,
        ratio: preset.aspectRatio,
        customization: preset.customization,
        isPreset: true,
        originalFormat: preset.format
      }));
      
      console.log('🔄 Found matching presets, selecting:', {
        currentPresetNames,
        matchingPresets: matchingPresets.map(p => p.name),
        initialSelected: initialSelected.map(p => p.name)
      });
    } else {
      // Fall back to default format matching
      const defaultFormats = [
        { id: '16:9', name: 'YouTube', ratio: '16/9' },
        { id: '9:16', name: 'Shorts', ratio: '9/16' },
        { id: '1:1', name: 'Instagram', ratio: '1/1' },
        { id: '4:5', name: 'Instagram', ratio: '4/5' }
      ];
      
      initialSelected = defaultFormats.filter(f => currentFormats.includes(f.id));
    }
    
    setSelectedMultiFormats(initialSelected);
    setShowFormatSelector(true);
  };

  // Add subtitle selector function
  const openSubtitleSelector = (type, data) => {
    console.log('🎬 openSubtitleSelector called with:', { type, data: { id: data.id, name: data.name } });
    setSelectedTarget({ type, data });
    
    // Get current subtitle presets for this item
    let currentSubtitleNames = [];
    let currentSubtitleIds = [];
    
    if (type === 'group') {
      const groupData = data;
      if (groupData.id === 'individual-parts') {
        // Check individual parts for subtitle presets
        const subtitleNames = new Set();
        const subtitleIds = new Set();
        parts.forEach(part => {
          if (part.subtitlePresetNames && part.subtitlePresetNames.length > 0) {
            part.subtitlePresetNames.forEach(name => subtitleNames.add(name));
          }
          if (part.subtitlePresets && part.subtitlePresets.length > 0) {
            part.subtitlePresets.forEach(id => subtitleIds.add(id));
          }
        });
        currentSubtitleNames = Array.from(subtitleNames);
        currentSubtitleIds = Array.from(subtitleIds);
      } else {
        // Get group subtitle presets
        currentSubtitleNames = groupData.subtitlePresetNames || [];
        currentSubtitleIds = groupData.subtitlePresets || [];
      }
    }
    
    // Load saved subtitle presets to check if current selection is from a preset
    const savedSubtitlePresets = getSavedSubtitlePresets();
    
    // Initialize selected subtitles
    let initialSelected = [];
    
    if (currentSubtitleIds.length > 0 || currentSubtitleNames.length > 0) {
      // Find all matching presets by ID or name and select them
      const matchingPresets = savedSubtitlePresets.filter(preset => 
        currentSubtitleIds.includes(preset.id) ||
        currentSubtitleNames.includes(preset.id) ||
        currentSubtitleNames.includes(preset.name)
      );
      
      initialSelected = matchingPresets.map(preset => ({
        id: preset.id, // ✅ Use original ID, don't add prefix
        name: preset.name,
        settings: preset.settings,
        isPreset: true
      }));
      
      console.log('🔄 Found matching subtitle presets, selecting:', {
        currentSubtitleNames,
        matchingPresets: matchingPresets.map(p => p.name),
        initialSelected: initialSelected.map(p => p.name)
      });
    } else {
      // No need for fallback - we only use saved presets now
      initialSelected = [];
    }
    
    setSelectedSubtitlePresets(initialSelected);
    setIsSubtitleSelectorOpen(true);
    console.log('🎬 Subtitle selector modal opened with initial selection:', initialSelected.map(p => ({ id: p.id, name: p.name })));
  };

  // Add subtitle selection state
  const [isSubtitleSelectorOpen, setIsSubtitleSelectorOpen] = useState(false);
  const [selectedSubtitlePresets, setSelectedSubtitlePresets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);

  // Get saved subtitle presets from localStorage (like watermark and format presets)
  const getSavedSubtitlePresets = () => {
    try {
      return JSON.parse(localStorage.getItem('subtitlePresets') || '[]');
    } catch {
      return [];
    }
  };

  // 🔧 Helper function to create proper subtitle export structure
  const createSubtitleExportStructure = (subtitleSettings, subtitleLanguage, startTime, endTime) => {
    console.log('🎨 Creating structured subtitle export with all settings:', {
      backgroundColor: subtitleSettings.backgroundColor,
      backgroundOpacity: subtitleSettings.backgroundOpacity,
      maxWordsPerLine: subtitleSettings.maxWordsPerLine,
      fontFamily: subtitleSettings.fontFamily,
      position: subtitleSettings.position,
      customPosition: subtitleSettings.customPosition,
      textShadow: subtitleSettings.textShadow,
      enabled: subtitleSettings.enabled,
      fontSize: subtitleSettings.fontSize,
      fontColor: subtitleSettings.fontColor
    });
    
    const structuredExport = {
      enabled: subtitleSettings.enabled,
      settings: subtitleSettings, // Complete settings object with all advanced properties
      language: subtitleLanguage,
      startTime: startTime,
      endTime: endTime
    };
    
    console.log('🎬 Complete structured subtitle object:', JSON.stringify(structuredExport, null, 2));
    
    return structuredExport;
  };

  // Handle subtitle selection
  const handleSubtitleSelection = (presetId) => {
    const selectedPreset = subtitlePresets.find(p => p.id === presetId);
    if (!selectedPreset) return;

    const { type, data } = selectedTarget;
    
    if (type === 'group') {
      const targetId = data.id === 'individual-parts' ? 'individual-parts' : data.id;
      
      if (targetId === 'individual-parts') {
        // Apply to all individual parts
        const updatedParts = parts.map(part => ({
          ...part,
          subtitlePreset: presetId,
          subtitlePresetName: selectedPreset.name,
          subtitleSettings: selectedPreset.settings
        }));
        
        onUpdate({
          ...project,
          parts: updatedParts
        });
      } else {
        // Apply to specific group
        const updatedGroups = groups.map(group => 
          group.id === targetId 
            ? {
                ...group,
                subtitlePreset: presetId,
                subtitlePresetName: selectedPreset.name,
                subtitleSettings: selectedPreset.settings
              }
            : group
        );
        
        onUpdate({
          ...project,
          groups: updatedGroups
        });
      }
    }
    
    setIsSubtitleSelectorOpen(false);
  };

  const handleMultiSubtitleSelection = () => {
    console.log('🎬 handleMultiSubtitleSelection called with:', {
      selectedTarget,
      selectedSubtitlePresets: selectedSubtitlePresets.map(p => ({ id: p.id, name: p.name }))
    });
    
    if (!selectedTarget) return;

    // Get subtitle settings from localStorage
    const getSubtitleSettingsFromPreset = (presetId) => {
      console.log('🔍 getSubtitleSettingsFromPreset called with presetId:', presetId);
      try {
        const savedPresets = JSON.parse(localStorage.getItem('subtitlePresets') || '[]');
        console.log('🔍 Available presets in localStorage:', savedPresets.map(p => ({ id: p.id, name: p.name })));
        const preset = savedPresets.find(p => p.id === presetId);
        console.log('🔍 Found preset:', preset ? { id: preset.id, name: preset.name } : 'NOT FOUND');
        
        if (preset) {
          // Handle both old format (with nested settings) and new format (direct properties)
          let subtitleSettings;
          
          if (preset.settings) {
            // Old format: settings are nested in a settings object
            subtitleSettings = {
              enabled: preset.settings.enabled !== undefined ? preset.settings.enabled : true,
              maxWordsPerLine: preset.settings.maxWordsPerLine !== undefined ? preset.settings.maxWordsPerLine : 8,
              fontSize: preset.settings.fontSize !== undefined ? preset.settings.fontSize : 36,
              fontColor: preset.settings.fontColor || '#ffff00',
              backgroundColor: preset.settings.backgroundColor || '#000000',
              backgroundOpacity: preset.settings.backgroundOpacity !== undefined ? preset.settings.backgroundOpacity : 90,
              position: preset.settings.position || 'bottom-center'
            };
          } else {
            // New format: settings are stored directly in preset object (WITH ADVANCED FEATURES)
            subtitleSettings = {
              enabled: preset.enabled !== undefined ? preset.enabled : true,
              maxWordsPerLine: preset.maxWordsPerLine !== undefined ? preset.maxWordsPerLine : 8,
              fontSize: preset.fontSize !== undefined ? preset.fontSize : 36,
              fontColor: preset.fontColor || '#ffff00',
              backgroundColor: preset.backgroundColor || '#000000',
              backgroundOpacity: preset.backgroundOpacity !== undefined ? preset.backgroundOpacity : 90,
              position: preset.position || 'bottom-center',
              // 🆕 ADVANCED FEATURES FROM PRESET
              customPosition: preset.customPosition || {
                enabled: false,
                x: 50,
                y: 90
              },
              fontFamily: preset.fontFamily || 'Arial',
              customFont: preset.customFont || null,
              textShadow: preset.textShadow || {
                enabled: true,
                color: '#000000',
                blur: 2,
                offsetX: 1,
                offsetY: 1
              }
            };
          }
          
          console.log(`🎬 Found subtitle settings WITH ADVANCED FEATURES for preset "${preset.name}" (${presetId}):`, subtitleSettings);
          return subtitleSettings;
        } else {
          console.warn(`🎬 Subtitle preset not found: ${presetId}`);
          return null;
        }
      } catch (error) {
        console.error('🎬 Error loading subtitle preset settings:', error);
        return null;
      }
    };

    if (selectedTarget.type === 'group') {
      const groupData = selectedTarget.data;
      
      if (groupData.id === 'individual-parts') {
        // Apply to all individual parts
        const updatedParts = parts.map(part => {
          if (selectedSubtitlePresets.length === 0) {
            // Clear subtitles
            return {
              ...part,
              subtitlePresets: null,
              subtitlePresetNames: null,
              subtitleSettings: null
            };
          } else {
            // Get settings from first preset
            const firstPresetSettings = getSubtitleSettingsFromPreset(selectedSubtitlePresets[0].id);
            
            // Apply selected presets
            return {
              ...part,
              subtitlePresets: selectedSubtitlePresets.map(p => p.id),
              subtitlePresetNames: selectedSubtitlePresets.map(p => p.name),
              subtitleSettings: firstPresetSettings // Use actual settings from localStorage
            };
          }
        });
        
        console.log('🎬 Updated individual parts with subtitle settings:', updatedParts.filter(p => p.subtitleSettings).map(p => ({
          id: p.id,
          name: p.name,
          subtitleSettings: p.subtitleSettings
        })));
        
        onUpdate({
          ...project,
          parts: updatedParts
        });
        
      } else {
        // Apply to specific group
        const updatedGroups = groups.map(group => {
          if (group.id === groupData.id) {
            if (selectedSubtitlePresets.length === 0) {
              // Clear subtitles
              return {
                ...group,
                subtitlePresets: null,
                subtitlePresetNames: null,
                subtitleSettings: null
              };
            } else {
              // 🎯 FIX: Use the specific preset selected for this group, not the first one
              const selectedPresetId = selectedSubtitlePresets.length === 1 
                ? selectedSubtitlePresets[0].id 
                : selectedSubtitlePresets[0].id; // For now, use first if multiple (shouldn't happen for single group)
              
              const groupPresetSettings = getSubtitleSettingsFromPreset(selectedPresetId);
              
              console.log(`🎬 Applying subtitle settings to group "${group.name}" with preset ID "${selectedPresetId}":`, groupPresetSettings);
              console.log(`🎨 Selected preset color: ${groupPresetSettings?.fontColor}, Group: ${group.name}`);
              
              // 🔍 CRITICAL DEBUG: Log the exact group being updated
              const updatedGroup = {
                ...group,
                subtitlePresets: [selectedPresetId], // Store only the selected preset
                subtitlePresetNames: [selectedSubtitlePresets[0].name], // Store only the selected preset name
                subtitleSettings: groupPresetSettings // Use the correct preset settings
              };
              
              console.log(`🔍 UPDATED GROUP "${group.name}":`, {
                id: updatedGroup.id,
                name: updatedGroup.name,
                subtitlePresets: updatedGroup.subtitlePresets,
                subtitlePresetNames: updatedGroup.subtitlePresetNames,
                subtitleSettings: updatedGroup.subtitleSettings,
                hasSubtitleSettings: !!updatedGroup.subtitleSettings
              });
              
              return updatedGroup;
            }
          }
          return group;
        });
        
        console.log('🎬 Updated groups with subtitle settings:', updatedGroups.filter(g => g.subtitleSettings).map(g => ({
          id: g.id,
          name: g.name,
          subtitleSettings: g.subtitleSettings
        })));
        
        console.log('🎬 Calling onUpdate with updated groups:', updatedGroups.map(g => ({
          id: g.id,
          name: g.name,
          subtitleSettings: g.subtitleSettings,
          subtitlePresetNames: g.subtitlePresetNames
        })));
        
        // 🔒 FORCE BACKUP: Save subtitle settings immediately to prevent loss
        console.log('🔒 Force saving subtitle settings to backup...');
        
        // 🎯 CRITICAL DEBUG: Log what we're about to save
        console.log('🎯 CRITICAL: About to save groups with subtitle settings:', {
          totalGroups: updatedGroups.length,
          groupsWithSubtitles: updatedGroups.filter(g => g.subtitleSettings).length,
          groupDetails: updatedGroups.map(g => ({
            id: g.id,
            name: g.name,
            hasSubtitleSettings: !!g.subtitleSettings,
            fontColor: g.subtitleSettings?.fontColor,
            presetNames: g.subtitlePresetNames,
            fullSubtitleSettings: g.subtitleSettings
          }))
        });
        
        // 🚨 CRITICAL: Log the exact data being passed to onUpdate
        console.log('🚨 EXACT onUpdate data:', {
          projectId: project.id,
          groupsData: updatedGroups.map(g => ({
            id: g.id,
            name: g.name,
            subtitleSettings: g.subtitleSettings,
            subtitlePresetNames: g.subtitlePresetNames,
            hasSubtitleSettings: !!g.subtitleSettings
          }))
        });
        
        onUpdate({
          ...project,
          groups: updatedGroups
        });
        
        // Force immediate backup to prevent subtitle settings from being lost
        setTimeout(() => {
          if (project?.id) {
            backupPartsAndGroups(project.id, parts, updatedGroups, individualPartsLanguage);
            console.log('💾 Forced backup after subtitle selection');
          }
        }, 100);
      }
    }
    
    setIsSubtitleSelectorOpen(false);
    setSelectedTarget(null);
    setSelectedSubtitlePresets([]);
  };

  // Get subtitle display name
  const getSubtitleDisplayName = (presetId) => {
    const preset = subtitlePresets.find(p => p.id === presetId);
    return preset ? preset.name : presetId;
  };

  const handleFormatSelection = (format) => {
    console.log('🔍 handleFormatSelection called with:', {
      format: format,
      isPreset: format.isPreset || false,
      formatId: format.id,
      formatName: format.name
    });
    console.log('🔍 Current selectedMultiFormats:', selectedMultiFormats.map(f => ({
      id: f.id,
      name: f.name,
      isPreset: f.isPreset || false
    })));
    
    // Add format to selection instead of replacing
    setSelectedMultiFormats(prev => {
      const isAlreadySelected = prev.some(f => f.id === format.id);
      console.log('🔍 Is already selected?', isAlreadySelected, 'for format:', format.id);
      
      if (isAlreadySelected) {
        // Remove if already selected (toggle)
        const newSelection = prev.filter(f => f.id !== format.id);
        console.log('✂️ Removing format, new selection:', newSelection.map(f => ({
          id: f.id,
          name: f.name,
          isPreset: f.isPreset || false
        })));
        return newSelection;
      } else {
        // Add to selection
        const newSelection = [...prev, format];
        console.log('➕ Adding format, new selection:', newSelection.map(f => ({
          id: f.id,
          name: f.name,
          isPreset: f.isPreset || false
        })));
        return newSelection;
      }
    });
    // Don't close modal - let user continue selecting
  };

  // Helper function to get display name for format
  const getFormatDisplayName = (formatId) => {
    const formatMap = {
      '16:9': 'YT',
      '9:16': 'Shorts', 
      '1:1': 'IG',
      '4:5': 'IGP'
    };
    return formatMap[formatId] || formatId;
  };

  const handleMultiFormatSelection = () => {
    if (!formatAction || selectedMultiFormats.length === 0) return;

    if (formatAction.type === 'group') {
      const groupData = formatAction.data;
      
      // Process selected formats to handle presets correctly
      const processedFormats = selectedMultiFormats.map(f => ({
        id: f.isPreset ? f.originalFormat : f.id, // Use original format for presets
        ratio: f.ratio,
        customization: f.customization,
        isPreset: f.isPreset || false,
        presetName: f.isPreset ? f.name : null, // Store preset name if it's a preset
        presetId: f.isPreset ? f.id : null // Store preset ID for easier matching
      }));
      
      console.log('🔄 Processing selected formats:', {
        original: selectedMultiFormats,
        processed: processedFormats
      });
      
      if (groupData.id === 'individual-parts') {
        // Apply formats to individual parts
        const updatedParts = parts.map(part => {
                      if (processedFormats.length === 1) {
              // Single format - use old format
              const format = processedFormats[0];
              return {
                ...part,
                format: format.id,
                aspectRatio: format.ratio,
                formats: null, // Clear formats array
                customization: format.customization,
                presetName: format.presetName, // Store preset name
                presetNames: processedFormats.filter(f => f.isPreset).map(f => f.presetName).filter(Boolean), // Store all preset names
                formatNames: processedFormats.map(f => f.isPreset ? f.presetName : getFormatDisplayName(f.id)).filter(Boolean) // Store all format display names
              };
            } else {
              // Multiple formats - use formats array
              return {
                ...part,
                formats: processedFormats.map(f => f.id),
                format: processedFormats[0]?.id, // Keep first as primary
                aspectRatio: processedFormats[0]?.ratio,
                customization: processedFormats[0]?.customization,
                presetName: processedFormats[0]?.presetName, // Store preset name of first format
                presetNames: processedFormats.filter(f => f.isPreset).map(f => f.presetName).filter(Boolean), // Store all preset names
                formatNames: processedFormats.map(f => f.isPreset ? f.presetName : getFormatDisplayName(f.id)).filter(Boolean) // Store all format display names
              };
            }
        });
        
        onUpdate({ parts: updatedParts });
        
      } else {
        // Apply formats to specific group
        const updatedGroups = groups.map(group => {
          if (group.id === groupData.id) {
            if (processedFormats.length === 1) {
              // Single format - use old format
              const format = processedFormats[0];
              return {
                ...group,
                format: format.id,
                aspectRatio: format.ratio,
                formats: null, // Clear formats array
                customization: format.customization,
                presetName: format.presetName, // Store preset name
                presetNames: processedFormats.filter(f => f.isPreset).map(f => f.presetName).filter(Boolean), // Store all preset names
                formatNames: processedFormats.map(f => f.isPreset ? f.presetName : getFormatDisplayName(f.id)).filter(Boolean) // Store all format display names
              };
            } else {
              // Multiple formats - use formats array
              return {
                ...group,
                formats: processedFormats.map(f => f.id),
                format: processedFormats[0]?.id, // Keep first as primary
                aspectRatio: processedFormats[0]?.ratio,
                customization: processedFormats[0]?.customization,
                presetName: processedFormats[0]?.presetName, // Store preset name of first format
                presetNames: processedFormats.filter(f => f.isPreset).map(f => f.presetName).filter(Boolean), // Store all preset names
                formatNames: processedFormats.map(f => f.isPreset ? f.presetName : getFormatDisplayName(f.id)).filter(Boolean) // Store all format display names
              };
            }
          }
          return group;
        });
        
        onUpdate({ groups: updatedGroups });
      }
    }

    setShowFormatSelector(false);
    setFormatAction(null);
    setSelectedMultiFormats([]);
  };

  const handleMultiWatermarkSelection = () => {
    if (!watermarkAction) return;
    
    // Handle clearing watermarks when no watermarks are selected
    if (selectedMultiWatermarks.length === 0) {
      console.log('🗑️ Clearing all watermarks for:', watermarkAction.type, watermarkAction.data?.name);
      
      if (watermarkAction.type === 'group') {
        const groupData = watermarkAction.data;
        
        if (groupData.id === 'individual-parts') {
          // Clear watermarks from all individual parts
          const updatedParts = parts.map(part => ({
            ...part,
            watermark: null,
            watermarkSettings: null,
            watermarks: null,
            watermarkPresetName: null,
            watermarkNames: [],
            watermarkDisplayNames: []
          }));
          
          onUpdate({ parts: updatedParts });
          console.log('🗑️ Cleared watermarks from all individual parts');
        } else {
          // Clear watermarks from specific group
          const updatedGroups = groups.map(group => {
            if (group.id === groupData.id) {
              return {
                ...group,
                watermark: null,
                watermarkSettings: null,
                watermarks: null,
                watermarkPresetName: null,
                watermarkNames: [],
                watermarkDisplayNames: []
              };
            }
            return group;
          });
          
          onUpdate({ groups: updatedGroups });
          console.log('🗑️ Cleared watermarks from group:', groupData.name);
        }
      }
      
      setShowWatermarkSelector(false);
      setWatermarkAction(null);
      setSelectedMultiWatermarks([]);
      return;
    }

    if (watermarkAction.type === 'group') {
      const groupData = watermarkAction.data;
      
      // Process selected watermarks to handle presets correctly
      const processedWatermarks = selectedMultiWatermarks.map(w => ({
        id: w.id, // Use the ID directly (which is the preset ID for presets)
        settings: w.settings,
        isPreset: w.isPreset || false,
        presetName: w.isPreset ? w.name : null,
        presetId: w.isPreset ? w.id : null
      }));
      
      console.log('🔄 Processing selected watermarks:', {
        original: selectedMultiWatermarks,
        processed: processedWatermarks
      });
      
      if (groupData.id === 'individual-parts') {
        // Apply watermarks to individual parts
        const updatedParts = parts.map(part => {
          if (processedWatermarks.length === 1) {
            // Single watermark - use old watermark field
            const watermark = processedWatermarks[0];
            return {
              ...part,
              watermark: watermark.id,
              watermarkSettings: watermark.settings,
              watermarks: null, // Clear watermarks array
              watermarkPresetName: watermark.presetName,
              watermarkNames: processedWatermarks.map(w => w.id).filter(Boolean),
              watermarkDisplayNames: processedWatermarks.map(w => w.presetName || getWatermarkDisplayName(w.id)).filter(Boolean)
            };
          } else {
            // Multiple watermarks - use watermarks array
            return {
              ...part,
              watermarks: processedWatermarks.map(w => w.id),
              watermark: processedWatermarks[0]?.id, // Keep first as primary
              watermarkSettings: processedWatermarks[0]?.settings,
              watermarkPresetName: processedWatermarks[0]?.presetName,
              watermarkNames: processedWatermarks.map(w => w.id).filter(Boolean),
              watermarkDisplayNames: processedWatermarks.map(w => w.presetName || getWatermarkDisplayName(w.id)).filter(Boolean)
            };
          }
        });
        
        onUpdate({ parts: updatedParts });
        
      } else {
        // Apply watermarks to specific group
        const updatedGroups = groups.map(group => {
          if (group.id === groupData.id) {
            if (processedWatermarks.length === 1) {
              // Single watermark
              const watermark = processedWatermarks[0];
              return {
                ...group,
                watermark: watermark.id,
                watermarkSettings: watermark.settings,
                watermarks: null, // Clear watermarks array
                watermarkPresetName: watermark.presetName,
                watermarkNames: processedWatermarks.map(w => w.id).filter(Boolean),
                watermarkDisplayNames: processedWatermarks.map(w => w.presetName || getWatermarkDisplayName(w.id)).filter(Boolean)
              };
            } else {
              // Multiple watermarks
              return {
                ...group,
                watermarks: processedWatermarks.map(w => w.id),
                watermark: processedWatermarks[0]?.id, // Keep first as primary
                watermarkSettings: processedWatermarks[0]?.settings,
                watermarkPresetName: processedWatermarks[0]?.presetName,
                watermarkNames: processedWatermarks.map(w => w.id).filter(Boolean),
                watermarkDisplayNames: processedWatermarks.map(w => w.presetName || getWatermarkDisplayName(w.id)).filter(Boolean)
              };
            }
          }
          return group;
        });
        
        onUpdate({ groups: updatedGroups });
      }
    }

    setShowWatermarkSelector(false);
    setWatermarkAction(null);
    setSelectedMultiWatermarks([]);
  };

  const getWatermarkDisplayName = (watermarkId) => {
    if (!watermarkId || watermarkId === 'none') return 'No Watermark';
    
    // Try to find in localStorage presets first
    try {
      const savedPresets = JSON.parse(localStorage.getItem('watermarkPresets') || '[]');
      const preset = savedPresets.find(p => p.id === watermarkId);
      if (preset) return preset.name;
    } catch (e) {
      console.error('Error reading watermark presets:', e);
    }

    const watermarkNames = {
      'none': 'No Watermark',
      'text-logo': 'Text Logo',
      'image-logo': 'Image Logo'
    };
    return watermarkNames[watermarkId] || (typeof watermarkId === 'string' ? (watermarkId.length > 10 ? `WM ${watermarkId.substring(0, 8)}` : watermarkId) : 'Watermark');
  };

  // Handle language selection
  const handleLanguageSelection = (langCode) => {
    if (!languageAction) return;
    
    const { type, data } = languageAction;
    const langInfo = getLanguageInfo(langCode);
    
                  console.log('🌐 Language selection:', { 
                type, 
                langCode, 
                langInfo,
                dataId: data.id,
                dataName: data.name || data.text?.substring(0, 30),
                currentParts: parts.length,
                currentGroups: groups.length
              });
    
    if (type === 'part') {
      // Update part with language
      const updatedPart = {
        ...data,
        language: langCode,
        isTranslated: true,
        updatedAt: new Date().toISOString()
      };
      
      // Try to find part in individual parts first
      const partInIndividualParts = parts.find(part => part.id === data.id);
      
      console.log('🔍 Looking for part:', {
        partId: data.id,
        foundInIndividual: !!partInIndividualParts,
        totalIndividualParts: parts.length,
        totalGroups: groups.length
      });
      
      if (partInIndividualParts) {
        // Update in individual parts
        const updatedParts = parts.map(part => 
          part.id === data.id ? updatedPart : part
        );
        console.log('✅ Updating part in individual parts:', {
          partId: data.id,
          oldLanguage: partInIndividualParts.language,
          newLanguage: langCode
        });
        onUpdate({ parts: updatedParts });
        
        // Verify update happened
        setTimeout(() => {
          console.log('🔍 Verification - Updated parts:', 
            updatedParts.filter(p => p.id === data.id).map(p => ({ id: p.id, language: p.language }))
          );
        }, 100);
      } else {
        // Update part in groups - check all groups
        let foundInGroup = false;
        const updatedGroups = groups.map(group => {
          const partInThisGroup = (group.parts || []).find(p => p.id === data.id);
          if (partInThisGroup) {
            foundInGroup = true;
            console.log('✅ Updating part in group:', {
              groupName: group.name,
              partId: data.id,
              oldLanguage: partInThisGroup.language,
              newLanguage: langCode
            });
            return {
              ...group,
              parts: (group.parts || []).map(part => 
                part.id === data.id ? updatedPart : part
              )
            };
          }
          return group;
        });
        
        if (foundInGroup) {
          onUpdate({ groups: updatedGroups });
          
          // Verify update happened
          setTimeout(() => {
            console.log('🔍 Verification - Updated groups:');
            updatedGroups.forEach(group => {
              const targetPart = (group.parts || []).find(p => p.id === data.id);
              if (targetPart) {
                console.log(`Group ${group.name}: part ${data.id} language = ${targetPart.language}`);
              }
            });
          }, 100);
        } else {
          console.error('❌ Part not found anywhere:', data.id);
        }
      }
      
      alert(`✅ Language set to ${langInfo.flag} ${langInfo.name}\n\nPart: "${data.text?.substring(0, 50)}..."`);
    } else if (type === 'segment') {
      // Update segment language (this would typically trigger translation)
      alert(`🔄 Translation to ${langInfo.flag} ${langInfo.name} would be initiated for this segment.\n\nSegment: "${data.text?.substring(0, 50)}..."`);
    } else if (type === 'group') {
      // Set language for entire group
      if (data.id === 'individual-parts') {
        // Handle Individual Parts language
        console.log('✅ Setting Individual Parts language:', {
          oldLanguage: individualPartsLanguage,
          newLanguage: langCode
        });
        setIndividualPartsLanguage(langCode);
        
        // 🔧 CRITICAL FIX: Save to localStorage for export consistency
        // مسح القيم القديمة أولاً لتجنب التعليق
        localStorage.removeItem('individualPartsLanguage');
        localStorage.removeItem('currentUILanguage');
        localStorage.removeItem('lastExportLanguage');
        
        // تعيين القيم الجديدة
        localStorage.setItem('individualPartsLanguage', langCode);
        localStorage.setItem('currentUILanguage', langCode);
        localStorage.setItem('lastExportLanguage', langCode);
        
        console.log('💾 Language RESET and SAVED to localStorage:', {
          individualPartsLanguage: langCode,
          currentUILanguage: langCode,
          lastExportLanguage: langCode,
          clearedOldValues: true
        });
        
        alert(`✅ Individual Parts language set to ${langInfo.flag} ${langInfo.name}\n\nGroup: "${data.name}"\n\n🔧 Language saved for future exports | تم حفظ اللغة للتصديرات القادمة\n\n🔄 Old language cache cleared | تم مسح ذاكرة اللغة القديمة`);
      } else {
        // Handle regular group language
        const targetGroup = groups.find(g => g.id === data.id);
        console.log('✅ Setting group language:', {
          groupName: data.name,
          groupId: data.id,
          oldLanguage: targetGroup?.language,
          newLanguage: langCode
        });
        
        const updatedGroups = groups.map(group => 
          group.id === data.id ? {
            ...group,
            language: langCode,
            updatedAt: new Date().toISOString()
          } : group
        );
        onUpdate({ groups: updatedGroups });
        
        alert(`✅ Group language set to ${langInfo.flag} ${langInfo.name}\n\nGroup: "${data.name}"`);
      }
    }
    
    setShowLanguageSelector(false);
    setLanguageAction(null);
  };

  // Handle group selection for move action
  const handleGroupSelection = (groupId) => {
    if (!moveAction) return;
    
    const { type, data, from } = moveAction;
    
    if (type === 'part') {
      if (groupId === 'new') {
        handleCreateGroupWithPart(data);
      } else if (groupId === 'individual') {
        if (from) {
          // Move from group to individual parts
          handleMovePartFromGroupToIndividual(data, from);
        } else {
          // Add from individual parts to new individual (no-op)
          handleAddToIndividualParts(data);
        }
      } else {
        if (from) {
          // Move part between groups
          handleMovePartBetweenGroups(data, from, groupId);
        } else {
          // Add from individual parts to group
          handleAddPartToGroup(data, groupId);
        }
      }
    } else if (type === 'segment') {
      if (groupId === 'individual') {
        // Move segment to individual parts
        const newIndividualPart = {
          id: Date.now().toString(),
          name: data.text?.substring(0, 50) || 'Individual Part',
          startTime: data.startTime,
          endTime: data.endTime,
          duration: data.endTime - data.startTime,
          text: data.text,
          speaker: data.speaker,
          source: 'individual-from-group',
          originalSegmentId: data.id,
          createdAt: new Date().toISOString()
        };
        
        const updatedParts = [...parts, newIndividualPart];
        const updatedGroups = groups.map(g => {
          if (g.id === from) {
            return {
              ...g,
              segments: g.segments.filter(id => id !== data.id),
              updatedAt: new Date().toISOString()
            };
          }
          return g;
        }).filter(g => (g.segments?.length || 0) + (g.parts?.length || 0) > 0);
        
        onUpdate({ parts: updatedParts, groups: updatedGroups });
        alert(`✅ Successfully moved to Individual Parts!\n\n📄 Segment: "${data.text?.substring(0, 50)}..."\n⏱️ Duration: ${formatTime(data.endTime - data.startTime)}`);
      } else {
        handleMoveSegmentToGroup(data.id, from, groupId);
      }
    }
    
    setShowGroupSelector(false);
    setMoveAction(null);
  };

  // Move part from group to individual parts
  const handleMovePartFromGroupToIndividual = (part, fromGroupId) => {
    // Remove from group and add to individual parts
    const updatedGroups = groups.map(group => {
      if (group.id === fromGroupId) {
        return {
          ...group,
          parts: (group.parts || []).filter(p => p.id !== part.id),
          updatedAt: new Date().toISOString()
        };
      }
      return group;
    });
    // DON'T filter out empty groups - keep them
    
    // Add to individual parts
    const updatedParts = [...parts, part];
    
    try {
      onUpdate({ parts: updatedParts, groups: updatedGroups });
      console.log('✅ Successfully moved part from group to individual parts');
      
      const fromGroup = groups.find(g => g.id === fromGroupId)?.name;
      alert(`✅ Successfully moved to Individual Parts!\n\n📂 From: "${fromGroup}"\n📄 Part: "${part.text?.substring(0, 50)}..."\n⏱️ Duration: ${formatTime(part.endTime - part.startTime)}`);
    } catch (error) {
      console.error('❌ Failed to move part to individual parts:', error);
      alert('❌ Save failed after moving part. Changes may not be preserved.');
    }
  };

  // Move part between groups
  const handleMovePartBetweenGroups = (part, fromGroupId, toGroupId) => {
    const updatedGroups = groups.map(group => {
      if (group.id === fromGroupId) {
        // Remove from source group
        return {
          ...group,
          parts: (group.parts || []).filter(p => p.id !== part.id),
          updatedAt: new Date().toISOString()
        };
      }
      if (group.id === toGroupId) {
        // Add to target group (avoid duplicates)
        const existingParts = group.parts || [];
        if (!existingParts.some(p => p.id === part.id)) {
          return {
            ...group,
            parts: [...existingParts, part],
            updatedAt: new Date().toISOString()
          };
        }
      }
      return group;
    });
    // DON'T filter out empty groups - keep them
    
    try {
      onUpdate({ groups: updatedGroups });
      console.log('✅ Successfully moved part between groups');
      
      const fromGroup = groups.find(g => g.id === fromGroupId)?.name;
      const toGroup = groups.find(g => g.id === toGroupId)?.name;
      alert(`✅ Successfully moved!\n\n📂 From: "${fromGroup}"\n📁 To: "${toGroup}"\n📄 Part: "${part.text?.substring(0, 50)}..."`);
    } catch (error) {
      console.error('❌ Failed to move part between groups:', error);
      alert('❌ Save failed after moving part. Changes may not be preserved.');
    }
  };

  // Create new group with part
  const handleCreateGroupWithPart = (part) => {
    const groupName = window.prompt?.('Enter group name:') || `Group ${groups.length + 1}`;
    if (groupName && groupName.trim()) {
      const newGroup = {
        id: Date.now().toString(),
        name: groupName.trim(),
        segments: [], // Keep existing segments structure
        parts: [part], // Add parts array
        createdAt: new Date().toISOString()
      };
      
      const updatedGroups = [...groups, newGroup];
      onUpdate({ groups: updatedGroups });
      
      alert(`✅ Group "${groupName.trim()}" created with part!\n\nPart: "${part.text?.substring(0, 50)}..."\nDuration: ${formatTime(part.endTime - part.startTime)}`);
    }
  };

  const handlePlayGroup = (group) => {
    console.log('▶️ Playing group:', group.name);
    
    const totalParts = (group.segments?.length || 0) + (group.parts?.length || 0);
    if (totalParts === 0) return;
    
    let firstItem = null;
    let itemName = '';
    
    // Try to find the first item (segment or part with earliest startTime)
    const allItems = [];
    
    // Add segments
    (group.segments || []).forEach(segmentId => {
      const segment = transcript.find(t => t.id === segmentId);
      if (segment) {
        allItems.push({
          type: 'segment',
          data: segment,
          startTime: segment.startTime,
          endTime: segment.endTime
        });
      }
    });
    
    // Add parts
    (group.parts || []).forEach(part => {
      allItems.push({
        type: 'part',
        data: part,
        startTime: part.startTime,
        endTime: part.endTime
      });
    });
    
    // Sort by start time and get first item
    if (allItems.length > 0) {
      allItems.sort((a, b) => a.startTime - b.startTime);
      firstItem = allItems[0];
      itemName = firstItem.type === 'segment' 
        ? firstItem.data.text?.substring(0, 30) || 'Segment'
        : firstItem.data.text?.substring(0, 30) || 'Part';
    }
    
    if (firstItem && onSeek) {
      // Seek to the first item and start playing
      onSeek(firstItem.startTime);
      
      if (onPlayPause) {
        onPlayPause(true);
      }
      
      // Set up auto-play for first item
      setCurrentPlayingPart({
        startTime: firstItem.startTime,
        endTime: firstItem.endTime,
        name: `${group.name} - ${itemName}`,
        groupId: group.id,
        itemIndex: 0,
        totalParts: totalParts
      });
    }
  };

  const handleRemoveFromGroup = (groupId, segmentId) => {
    if (window.confirm('Remove this segment from the group?')) {
      console.log('🗑️ Removing segment from group:', { groupId, segmentId });
      
      const targetGroup = groups.find(g => g.id === groupId);
      console.log('🔍 Target group before segment removal:', {
        groupName: targetGroup?.name,
        currentParts: targetGroup?.parts?.length || 0,
        currentSegments: targetGroup?.segments?.length || 0
      });
      
      const updatedGroups = groups.map(group => {
        if (group.id === groupId) {
          const newSegments = group.segments.filter(id => id !== segmentId);
          console.log(`📝 Group "${group.name}" segments updated:`, {
            before: group.segments?.length || 0,
            after: newSegments.length
          });
          
          return {
            ...group,
            segments: newSegments,
            updatedAt: new Date().toISOString()
          };
        }
        return group;
      });
      // DON'T filter out empty groups - keep them
      
      console.log('✅ Groups after segment removal:', updatedGroups.map(g => ({
        name: g.name,
        parts: g.parts?.length || 0,
        segments: g.segments?.length || 0
      })));
      
              try {
          onUpdate({ groups: updatedGroups });
          console.log('✅ Successfully updated groups after segment removal');
          
          // Force backup after successful update - with validation
          setTimeout(() => {
            if (project?.id) {
              backupPartsAndGroups(project.id, parts, updatedGroups, individualPartsLanguage);
              console.log('💾 Backed up after segment removal');
            } else {
              console.warn('💾 Skip backup - invalid project ID');
            }
          }, 200); // Increased delay to avoid race condition
        } catch (error) {
          console.error('❌ Failed to update after segment removal:', error);
          console.error('❌ Error details:', {
            message: error.message,
            groupId,
            segmentId,
            updatedGroupsCount: updatedGroups.length
          });
          alert('❌ Save failed after removing segment. Please try again or check console for details.');
        }
    }
  };

  const handleRemovePartFromGroup = (groupId, partId) => {
    if (window.confirm('Remove this part from the group?')) {
      console.log('🗑️ Removing part from group:', { groupId, partId });
      
      const targetGroup = groups.find(g => g.id === groupId);
      const targetPart = targetGroup?.parts?.find(p => p.id === partId);
      
      console.log('🔍 Target group before removal:', {
        groupName: targetGroup?.name,
        currentParts: targetGroup?.parts?.length || 0,
        currentSegments: targetGroup?.segments?.length || 0
      });
      
      const updatedGroups = groups.map(group => {
        if (group.id === groupId) {
          const newParts = (group.parts || []).filter(part => part.id !== partId);
          console.log(`📝 Group "${group.name}" parts updated:`, {
            before: group.parts?.length || 0,
            after: newParts.length
          });
          
          return {
            ...group,
            parts: newParts,
            updatedAt: new Date().toISOString()
          };
        }
        return group;
      });
      // DON'T filter out empty groups - keep them
      
      console.log('✅ Groups after removal:', updatedGroups.map(g => ({
        name: g.name,
        parts: g.parts?.length || 0,
        segments: g.segments?.length || 0
      })));
      
      // Move the removed part back to individual parts
      if (targetPart) {
        const updatedParts = [...parts, targetPart];
        console.log('📄 Moving part back to individual parts:', targetPart.text?.substring(0, 30));
        
        try {
          onUpdate({ parts: updatedParts, groups: updatedGroups });
          console.log('✅ Successfully updated parts and groups');
          
          // Force backup after successful update - with validation
          setTimeout(() => {
            if (project?.id) {
              backupPartsAndGroups(project.id, updatedParts, updatedGroups, individualPartsLanguage);
              console.log('💾 Backed up after part removal');
            } else {
              console.warn('💾 Skip backup - invalid project ID');
            }
          }, 200); // Increased delay to avoid race condition
        } catch (error) {
          console.error('❌ Failed to update after part removal:', error);
          console.error('❌ Error details:', {
            message: error.message,
            groupId,
            partId,
            partsCount: updatedParts.length,
            groupsCount: updatedGroups.length
          });
          alert('❌ Save failed after removing part. Please try again or check console for details.');
        }
      } else {
        try {
          onUpdate({ groups: updatedGroups });
          console.log('✅ Successfully updated groups');
          
          // Force backup after successful update - with validation
          setTimeout(() => {
            if (project?.id) {
              backupPartsAndGroups(project.id, parts, updatedGroups, individualPartsLanguage);
              console.log('💾 Backed up after part removal');
            } else {
              console.warn('💾 Skip backup - invalid project ID');
            }
          }, 200); // Increased delay to avoid race condition
        } catch (error) {
          console.error('❌ Failed to update after part removal:', error);
          console.error('❌ Error details:', {
            message: error.message,
            groupId,
            partId,
            groupsCount: updatedGroups.length
          });
          alert('❌ Save failed after removing part. Please try again or check console for details.');
        }
      }
    }
  };

  const handleSeekOnlyToSegment = (segment) => {
    console.log('📍 Seeking only to segment (no auto-play):', segment.startTime);
    
    if (onSeek && typeof onSeek === 'function') {
      try {
        onSeek(segment.startTime);
        console.log('✅ Seek-only called successfully with time:', segment.startTime);
      } catch (error) {
        console.error('❌ Error calling onSeek:', error);
      }
    } else {
      console.error('❌ onSeek is not available:', { onSeek, type: typeof onSeek });
    }
  };

  const handleSeekOnlyToPart = (part) => {
    console.log('📍 Seeking only to part (no auto-play):', part.startTime);
    
    if (onSeek && typeof onSeek === 'function') {
      try {
        onSeek(part.startTime);
        console.log('✅ Seek-only called successfully with time:', part.startTime);
      } catch (error) {
        console.error('❌ Error calling onSeek:', error);
      }
    } else {
      console.error('❌ onSeek is not available:', { onSeek, type: typeof onSeek });
    }
  };

  const handleSeekToSegment = (segment) => {
    console.log('🎯 Seeking to segment:', {
      startTime: segment.startTime,
      endTime: segment.endTime,
      text: segment.text?.substring(0, 50),
      hasOnSeek: !!onSeek,
      onSeekType: typeof onSeek,
      currentlyPlaying: isPlaying
    });
    
    if (onSeek && typeof onSeek === 'function') {
      try {
        // First seek to the segment
        onSeek(segment.startTime);
        console.log('✅ onSeek called successfully with time:', segment.startTime);
        
        // Set this segment as currently playing part for auto-pause
        setCurrentPlayingPart({
          startTime: segment.startTime,
          endTime: segment.endTime,
          name: segment.text?.substring(0, 30) || 'Segment'
        });
        
        // Then start playing if not already playing
        if (!isPlaying && onPlayPause && typeof onPlayPause === 'function') {
          onPlayPause(true);
          console.log('▶️ Video started playing');
        } else {
          console.log('📹 Video was already playing, just seeked');
        }
      } catch (error) {
        console.error('❌ Error calling onSeek:', error);
      }
    } else {
      console.error('❌ onSeek is not available:', { onSeek, type: typeof onSeek });
    }
  };

  const handleSeekToPart = (part) => {
    console.log('🎯 Seeking to part:', {
      startTime: part.startTime,
      endTime: part.endTime,
      name: part.name,
      hasOnSeek: !!onSeek,
      onSeekType: typeof onSeek,
      currentlyPlaying: isPlaying
    });
    
    if (onSeek && typeof onSeek === 'function') {
      try {
        // First seek to the part
        onSeek(part.startTime);
        console.log('✅ onSeek called successfully with time:', part.startTime);
        
        // Set this part as currently playing part for auto-pause
        setCurrentPlayingPart({
          startTime: part.startTime,
          endTime: part.endTime,
          name: part.name || 'Part'
        });
        
        // Then start playing if not already playing
        if (!isPlaying && onPlayPause && typeof onPlayPause === 'function') {
          onPlayPause(true);
          console.log('▶️ Video started playing');
        } else {
          console.log('📹 Video was already playing, just seeked');
        }
      } catch (error) {
        console.error('❌ Error calling onSeek:', error);
      }
    } else {
      console.error('❌ onSeek is not available:', { onSeek, type: typeof onSeek });
    }
  };



  const handleExportAllTranscriptParts = async () => {
    return exportAllPartsService(transcriptParts, project, transcript, onUpdate);
  };

  const handlePlayAllTranscriptParts = () => {
    console.log('▶️ Playing all transcript parts:', transcriptParts);
    
    if (transcriptParts.length === 0) return;
    
    // Start with the first transcript part
    const firstPart = transcriptParts[0];
    if (onSeek) {
      onSeek(firstPart.startTime);
      
      if (onPlayPause) {
        onPlayPause(true);
      }
      
      setCurrentPlayingPart({
        startTime: firstPart.startTime,
        endTime: firstPart.endTime,
        name: firstPart.text?.substring(0, 30) || 'Transcript Part',
        isSequential: true,
        partIndex: 0,
        totalParts: transcriptParts.length
      });
    }
  };

  const handleDeleteAllTranscriptParts = () => {
    if (transcriptParts.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete all ${transcriptParts.length} individual parts from transcript?`)) {
      // Keep only manual parts
      const updatedParts = parts.filter(part => !part.source || part.source !== 'transcript');
      onUpdate({ parts: updatedParts });
      
      console.log(`🗑️ Deleted ${transcriptParts.length} transcript parts`);
    }
  };
  const handleExportAllIndividualParts = async () => {
    return exportAllPartsService(individualParts, project, transcript, onUpdate);
  };




                

                





  // Get translated text for segment based on language
  const getTranslatedSegmentText = (segmentId, language = 'original') => {
    const segment = transcript.find(t => t.id === segmentId);
    if (!segment) return 'Unknown segment';
    
    // If requesting original language or no translations available
    if (language === 'original' || !project?.translations) {
      return segment.text;
    }
    
    // Check for new format translations
    const translations = project.translations[language];
    if (translations && translations.length > 0) {
      // Find matching translation segment by time range
      const translatedSegment = translations.find(ts => 
        Math.abs(ts.startTime - segment.startTime) < 0.5 && 
        Math.abs(ts.endTime - segment.endTime) < 0.5
      );
      
      if (translatedSegment) {
        console.log(`🌍 Found translation for segment ${segmentId}: ${language} -> "${translatedSegment.text}"`);
        return translatedSegment.text;
      }
    }
    
    // Check for legacy translation format
    if (language === project?.translationLanguage && project?.translation?.length > 0) {
      const legacyTranslation = project.translation.find(ts => 
        Math.abs(ts.startTime - segment.startTime) < 0.5 && 
        Math.abs(ts.endTime - segment.endTime) < 0.5
      );
      
      if (legacyTranslation) {
        console.log(`🌍 Found legacy translation for segment ${segmentId}: ${language} -> "${legacyTranslation.text}"`);
        return legacyTranslation.text;
      }
    }
    
    // Fallback to original text if translation not found
    console.log(`⚠️ Translation not found for segment ${segmentId} in ${language}, using original text`);
    return segment.text;
  };

  const getSegmentText = (segmentId, language = null) => {
    // Use the language parameter if provided, otherwise fallback to original
    const targetLanguage = language || 'original';
    return getTranslatedSegmentText(segmentId, targetLanguage);
  };

  const getSegmentTimeRange = (segmentId) => {
    const segment = transcript.find(t => t.id === segmentId);
    return segment ? `${formatTime(segment.startTime)} - ${formatTime(segment.endTime)}` : '0:00 - 0:00';
  };

  const handleGroupExpansion = (groupId) => {
    setExpandedGroups(prev => ({ 
      ...prev, 
      [groupId]: !prev[groupId] 
    }));
  };

  // Get translated text for parts (which may contain multiple segments)
  const getTranslatedPartText = (part, language = 'original') => {
    console.log(`🔍 getTranslatedPartText called: part ${part.id}, language: ${language}`);
    
    // If requesting original language, prioritize explicit text (user selection)
    const isOriginalLang = language === 'original' || 
                           language === project?.originalLanguage || 
                           language === project?.transcriptionLanguage || 
                           !language;
    if (isOriginalLang && part.text) {
      console.log(`📝 Using explicit part text for original language: "${part.text}"`);
      return part.text;
    }
    
    // Proportional word-boundary cropping helper
    const cropTranslationText = (text, startRatio, endRatio) => {
      if (!text) return "";
      
      const startChar = Math.floor(text.length * startRatio);
      const endChar = Math.floor(text.length * endRatio);
      
      let finalStart = startChar;
      if (startChar > 0) {
        const spaceBefore = text.lastIndexOf(' ', startChar);
        const spaceAfter = text.indexOf(' ', startChar);
        if (spaceBefore !== -1 && (startChar - spaceBefore < spaceAfter - startChar || spaceAfter === -1)) {
          finalStart = spaceBefore + 1;
        } else if (spaceAfter !== -1) {
          finalStart = spaceAfter + 1;
        }
      }
      
      let finalEnd = endChar;
      if (endChar < text.length) {
        const spaceBefore = text.lastIndexOf(' ', endChar);
        const spaceAfter = text.indexOf(' ', endChar);
        if (spaceBefore !== -1 && (endChar - spaceBefore < spaceAfter - endChar || spaceAfter === -1)) {
          finalEnd = spaceBefore;
        } else if (spaceAfter !== -1) {
          finalEnd = spaceAfter;
        }
      }
      
      return text.substring(finalStart, finalEnd).trim();
    };

    const hasMultipleSegments = part.originalSegments && part.originalSegments.length > 1;
    const shouldUseOriginalSegments = hasMultipleSegments || !part.text;
    
    // For parts created from segments, try to get text from segments first (especially multi-segment parts)
    if (shouldUseOriginalSegments && part.originalSegments && part.originalSegments.length > 0) {
      console.log(`📄 Part has ${part.originalSegments.length} original segments`);
      
      // If only one segment, use the existing logic
      if (part.originalSegments.length === 1) {
        const firstSegment = part.originalSegments[0];
        const firstSegmentId = typeof firstSegment === 'object' ? firstSegment.id : firstSegment;
        console.log(`📄 Single segment, ID: ${firstSegmentId}`);
        
        if (firstSegmentId) {
          const rawText = getTranslatedSegmentText(firstSegmentId, language);
          const segmentObj = typeof firstSegment === 'object' ? firstSegment : transcript.find(s => s.id === firstSegmentId);
          
          if (segmentObj && rawText && rawText !== 'Unknown segment' && part.text) {
            const segDuration = segmentObj.endTime - segmentObj.startTime;
            if (segDuration > 0) {
              const startRatio = (part.startTime - segmentObj.startTime) / segDuration;
              const endRatio = (part.endTime - segmentObj.startTime) / segDuration;
              return cropTranslationText(rawText, startRatio, endRatio);
            }
          }
          return rawText;
        } else {
          console.log(`⚠️ Could not get valid segment ID from:`, firstSegment);
        }
      } else {
        // Multiple segments - combine all translated texts
        console.log(`📄 Multiple segments (${part.originalSegments.length}), combining all...`);
        const translatedTexts = [];
        
        const sortedSegs = [...part.originalSegments].sort((a, b) => {
          const aStart = typeof a === 'object' ? a.startTime : (transcript.find(s => s.id === a)?.startTime || 0);
          const bStart = typeof b === 'object' ? b.startTime : (transcript.find(s => s.id === b)?.startTime || 0);
          return aStart - bStart;
        });
        
        sortedSegs.forEach((segment, idx) => {
          const segmentId = typeof segment === 'object' ? segment.id : segment;
          if (segmentId) {
            const rawText = getTranslatedSegmentText(segmentId, language);
            if (rawText && rawText !== 'Unknown segment') {
              const segmentObj = typeof segment === 'object' ? segment : transcript.find(s => s.id === segmentId);
              let text = rawText;
              
              if (segmentObj) {
                const segDuration = segmentObj.endTime - segmentObj.startTime;
                if (segDuration > 0) {
                  let startRatio = 0;
                  let endRatio = 1;
                  if (idx === 0 && segmentObj.startTime < part.startTime) {
                    startRatio = (part.startTime - segmentObj.startTime) / segDuration;
                  }
                  if (idx === sortedSegs.length - 1 && segmentObj.endTime > part.endTime) {
                    endRatio = (part.endTime - segmentObj.startTime) / segDuration;
                  }
                  
                  if (startRatio > 0 || endRatio < 1) {
                    text = cropTranslationText(text, startRatio, endRatio);
                  }
                }
              }
              
              if (text) {
                translatedTexts.push(text);
              }
            }
          }
        });
        
        if (translatedTexts.length > 0) {
          const combinedText = translatedTexts.join(' ');
          console.log(`✅ Combined text from ${translatedTexts.length} segments: "${combinedText?.substring(0, 80)}..."`);
          return combinedText;
        } else {
          console.log(`⚠️ No valid texts found from original segments`);
        }
      }
    }
    
    // If part has explicit text AND it's not a multi-segment part, use it
    if (part.text && !hasMultipleSegments) {
      if (language === 'original' || !project?.translations) {
        console.log(`📝 Using explicit part text (original): "${part.text}"`);
        return part.text;
      }
      
      // Try to get translation for explicit text
      const translations = project.translations[language];
      if (translations && translations.length > 0) {
        const matchingTranslation = translations.find(ts => 
          Math.abs(ts.startTime - part.startTime) < 0.5 && 
          Math.abs(ts.endTime - part.endTime) < 0.5
        );
        
        if (matchingTranslation) {
          console.log(`🌍 Found translation for part: ${language} -> "${matchingTranslation.text}"`);
          return matchingTranslation.text;
        }
      }
      
      console.log(`📝 Using explicit part text (no translation found): "${part.text}"`);
      return part.text;
    }
    
    // For manual parts without explicit text, find overlapping segments from transcript
    if (project.transcript && project.transcript.length > 0) {
      console.log(`🔍 Looking for overlapping segments for part ${part.startTime}-${part.endTime}`);
      const overlappingSegments = project.transcript.filter(segment => 
        // Check if segment overlaps with part time range
        (segment.startTime < part.endTime && segment.endTime > part.startTime)
      );
      
      console.log(`📊 Found ${overlappingSegments.length} overlapping segments`);
      
      if (overlappingSegments.length > 0) {
        // Sort segments by start time
        const sortedSegments = [...overlappingSegments].sort((a, b) => a.startTime - b.startTime);
        
        // Get translated text for each overlapping segment
        const translatedTexts = [];
        sortedSegments.forEach((segment, idx) => {
          const rawText = getSegmentText(segment.id, language);
          if (rawText && rawText !== 'Unknown segment') {
            const segDuration = segment.endTime - segment.startTime;
            let text = rawText;
            
            if (segDuration > 0) {
              let startRatio = 0;
              let endRatio = 1;
              if (idx === 0 && segment.startTime < part.startTime) {
                startRatio = (part.startTime - segment.startTime) / segDuration;
              }
              if (idx === sortedSegments.length - 1 && segment.endTime > part.endTime) {
                endRatio = (part.endTime - segment.startTime) / segDuration;
              }
              
              if (startRatio > 0 || endRatio < 1) {
                text = cropTranslationText(text, startRatio, endRatio);
              }
            }
            
            if (text) {
              translatedTexts.push(text);
            }
          }
        });
        
        if (translatedTexts.length > 0) {
          const combinedText = translatedTexts.join(' ');
          console.log(`✅ Combined translated text: "${combinedText}"`);
          return combinedText;
        } else {
          console.log(`⚠️ No valid translated texts found for overlapping segments`);
        }
      }
    }
    
    // Try to get translation by time range if no segments found
    if (language !== 'original' && project?.translations) {
      console.log(`🔍 Trying direct translation lookup for time range`);
      const translations = project.translations[language];
      if (translations && translations.length > 0) {
        const matchingTranslation = translations.find(ts => 
          Math.abs(ts.startTime - part.startTime) < 0.5 && 
          Math.abs(ts.endTime - part.endTime) < 0.5
        );
        
        if (matchingTranslation) {
          console.log(`🌍 Found direct translation for part: ${language} -> "${matchingTranslation.text}"`);
          return matchingTranslation.text;
        }
      }
    }
    
    // Check legacy translation format
    if (language === project?.translationLanguage && project?.translation?.length > 0) {
      console.log(`🔍 Checking legacy translation format`);
      const legacyTranslation = project.translation.find(ts => 
        Math.abs(ts.startTime - part.startTime) < 0.5 && 
        Math.abs(ts.endTime - part.endTime) < 0.5
      );
      
      if (legacyTranslation) {
        console.log(`🌍 Found legacy translation for part: ${language} -> "${legacyTranslation.text}"`);
        return legacyTranslation.text;
      }
    }
    
    // Fallback to name if available
    if (part.name && part.name !== 'Part') {
      console.log(`📝 Using part name as fallback: "${part.name}"`);
      return part.name;
    }
    
    // Final fallback
    console.warn(`⚠️ No text found for part ${part.id} (${formatTime(part.startTime)} - ${formatTime(part.endTime)})`);
    return 'Part';
  };

  // Add debug function to window for Electron debugging
  React.useEffect(() => {
    window.debugTranslation = function(partId, language) {
      const part = parts.find(p => p.id === partId);
      if (part) {
        console.log('🧪 DEBUG PART:', part);
        console.log('🌍 LANGUAGE:', language);
        console.log('📊 PROJECT TRANSLATIONS:', project?.translations);
        console.log('📄 TRANSCRIPT:', transcript);
        const result = getTranslatedPartText(part, language);
        console.log('📝 RESULT:', result);
        return result;
      } else {
        console.log('❌ Part not found:', partId);
        console.log('🔍 Available parts:', parts.map(p => p.id));
      }
    };
    
    window.debugProjectData = function() {
      console.log('🎯 PROJECT DATA:', project);
      console.log('📄 TRANSCRIPT:', transcript);
      console.log('📋 PARTS:', parts);
      console.log('🌍 TRANSLATIONS:', project?.translations);
    };
    
    return () => {
      delete window.debugTranslation;
      delete window.debugProjectData;
    };
  }, [parts, project, transcript]);

  // Helper function to resolve watermark settings from part or group
  const resolveWatermarkSettings = (item, context = 'unknown') => {
    // 🔧 NEW: Handle multiple watermarks first
    if (item.watermarks && Array.isArray(item.watermarks) && item.watermarks.length > 0) {
      console.log(`💧 Found multiple watermarks for ${context}:`, item.watermarks);
      
      try {
        const savedPresets = JSON.parse(localStorage.getItem('watermarkPresets') || '[]');
        const resolvedWatermarks = item.watermarks.map(watermarkId => {
          const preset = savedPresets.find(p => p.id === watermarkId);
          if (preset) {
            console.log(`💧 Resolved watermark preset: ${preset.name}`, preset.settings);
            return preset.settings;
          } else {
            console.warn(`💧 Watermark preset not found for ID: ${watermarkId}`);
            return null;
          }
        }).filter(Boolean); // Remove null values
        
        if (resolvedWatermarks.length > 0) {
          console.log(`💧 Returning ${resolvedWatermarks.length} resolved watermarks for ${context}`);
          return resolvedWatermarks; // Return array of watermark settings
        }
      } catch (error) {
        console.error(`💧 Error loading multiple watermark presets for ${context}:`, error);
      }
    }

    // Handle single watermark (legacy)
    if (!item.watermark && !item.watermarkSettings) {
      return null;
    }

    // Use watermarkSettings if available (it's the actual settings object)
    if (item.watermarkSettings) {
      console.log(`💧 Using watermarkSettings for ${context}:`, item.watermarkSettings);
      return item.watermarkSettings;
    }

    // Try to find watermark in saved presets
    if (item.watermark) {
      try {
        const savedPresets = JSON.parse(localStorage.getItem('watermarkPresets') || '[]');
        const preset = savedPresets.find(p => p.id === item.watermark);
        if (preset) {
          console.log(`💧 Found watermark preset for ${context}:`, preset.name, preset.settings);
          return preset.settings;
        } else {
          console.warn(`💧 Watermark preset not found for ${context} ID: ${item.watermark}`);
        }
      } catch (error) {
        console.error(`💧 Error loading watermark presets for ${context}:`, error);
      }
    }

    return null;
  };

  return (
    <Container>
      {/* Group Selector Modal */}
      {showGroupSelector && (
        <GroupSelectorModal onClick={() => setShowGroupSelector(false)}>
          <GroupSelectorContent onClick={(e) => e.stopPropagation()}>
            <GroupSelectorTitle>
              📁 Select destination for {moveAction?.type}
            </GroupSelectorTitle>
            
            {/* Individual Parts option - only show if not moving from Individual Parts */}
            {moveAction?.from !== null && (
              <GroupOption onClick={() => handleGroupSelection('individual')}>
                <GroupOptionTitle>
                  📄 Individual Parts
                </GroupOptionTitle>
                <GroupOptionInfo>
                  Move to standalone individual parts section
                </GroupOptionInfo>
              </GroupOption>
            )}
            
            {/* Existing groups */}
            {groups.filter(g => g.id !== moveAction?.from).map(group => (
              <GroupOption key={group.id} onClick={() => handleGroupSelection(group.id)}>
                <GroupOptionTitle>
                  📁 {group.name}
                </GroupOptionTitle>
                <GroupOptionInfo>
                  {(group.segments?.length || 0) + (group.parts?.length || 0)} parts
                </GroupOptionInfo>
              </GroupOption>
            ))}
            
            {/* Create new group option */}
            <NewGroupOption onClick={() => handleGroupSelection('new')}>
              <GroupOptionTitle>
                ➕ Create New Group
              </GroupOptionTitle>
              <GroupOptionInfo>
                Create a new group with this {moveAction?.type}
              </GroupOptionInfo>
            </NewGroupOption>
            
            <ModalActions>
              <ActionButton onClick={() => setShowGroupSelector(false)}>
                Cancel
              </ActionButton>
            </ModalActions>
          </GroupSelectorContent>
        </GroupSelectorModal>
      )}

      {/* Language Selector Modal */}
      {showLanguageSelector && (
        <LanguageSelectorModal onClick={() => setShowLanguageSelector(false)}>
          <LanguageSelectorContent onClick={(e) => e.stopPropagation()}>
            <LanguageSelectorTitle>
              🌐 Select language for {languageAction?.type}
            </LanguageSelectorTitle>
            
            {(() => {
              const usedLanguages = getUsedLanguages(project, individualPartsLanguage);
              const hasUsedLanguages = usedLanguages.length > 1; // More than just English
              const shouldShowAll = languageAction?.showAll;
              
              const currentSelectedLanguage = 
                languageAction?.type === 'group' && languageAction?.data?.id === 'individual-parts' ? individualPartsLanguage :
                languageAction?.data?.language;
              
              if (hasUsedLanguages && !shouldShowAll) {
                return (
                  <>
                    <div style={{ 
                      padding: '8px 12px', 
                      fontSize: '11px', 
                      color: '#666',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      textAlign: 'center'
                    }}>
                      📊 Languages used in this project
                    </div>
                    {usedLanguages.map(lang => {
                      return (
                        <LanguageOption 
                          key={lang.code} 
                          onClick={() => handleLanguageSelection(lang.code)}
                          style={lang.code === currentSelectedLanguage ? {
                            border: '2px solid #007bff',
                            backgroundColor: '#007bff10'
                          } : {
                            border: '1px solid #dee2e6'
                          }}
                        >
                          <LanguageOptionTitle style={{ color: lang.code === currentSelectedLanguage ? '#007bff' : 'inherit' }}>
                            {lang.flag} {lang.name}
                            {lang.code === currentSelectedLanguage && <span style={{ marginLeft: '8px', color: '#007bff', fontWeight: 'bold' }}>✓</span>}
                          </LanguageOptionTitle>
                          <LanguageOptionInfo>
                            {lang.code.toUpperCase()} - {lang.code === currentSelectedLanguage ? 'Currently selected' : `Set as ${languageAction?.type} language`}
                          </LanguageOptionInfo>
                        </LanguageOption>
                      );
                    })}
                    <div 
                      style={{ 
                        padding: '8px 12px', 
                        fontSize: '10px', 
                        color: '#007bff',
                        textAlign: 'center',
                        borderTop: '1px solid #eee',
                        marginTop: '8px',
                        cursor: 'pointer',
                        borderRadius: '4px'
                      }}
                      onClick={() => {
                        // Show all languages option
                        const showAll = window.confirm('📋 Show all available languages?\n\nCurrently showing only languages used in this project.');
                        if (showAll) {
                          // Re-render with all languages
                          setShowLanguageSelector(false);
                          setTimeout(() => {
                            setLanguageAction({ ...languageAction, showAll: true });
                            setShowLanguageSelector(true);
                          }, 100);
                        }
                      }}
                    >
                      ➕ Show more languages
                    </div>
                  </>
                );
                             } else {
                 // Show all languages if no languages used yet OR if showAll is requested
                 return (
                   <>
                     {shouldShowAll && hasUsedLanguages && (
                       <div style={{ 
                         padding: '8px 12px', 
                         fontSize: '11px', 
                         color: '#666',
                         backgroundColor: '#f8f9fa',
                         borderRadius: '4px',
                         marginBottom: '8px',
                         textAlign: 'center'
                       }}>
                         🌐 All available languages
                       </div>
                     )}
                     {SUPPORTED_LANGUAGES.map(lang => {
                       return (
                        <LanguageOption 
                          key={lang.code} 
                          onClick={() => handleLanguageSelection(lang.code)}
                          style={lang.code === currentSelectedLanguage ? {
                            border: '2px solid #007bff',
                            backgroundColor: '#007bff10'
                          } : {
                            border: '1px solid #dee2e6'
                          }}
                        >
                          <LanguageOptionTitle style={{ color: lang.code === currentSelectedLanguage ? '#007bff' : 'inherit' }}>
                            {lang.flag} {lang.name}
                            {lang.code === currentSelectedLanguage && <span style={{ marginLeft: '8px', color: '#007bff', fontWeight: 'bold' }}>✓</span>}
                          </LanguageOptionTitle>
                          <LanguageOptionInfo>
                            {lang.code.toUpperCase()} - {lang.code === currentSelectedLanguage ? 'Currently selected' : `Set as ${languageAction?.type} language`}
                          </LanguageOptionInfo>
                        </LanguageOption>
                      );
                    })}
                     {shouldShowAll && hasUsedLanguages && (
                       <div 
                         style={{ 
                           padding: '8px 12px', 
                           fontSize: '10px', 
                           color: '#6c757d',
                           textAlign: 'center',
                           borderTop: '1px solid #eee',
                           marginTop: '8px',
                           cursor: 'pointer'
                         }}
                         onClick={() => {
                           setShowLanguageSelector(false);
                           setTimeout(() => {
                             setLanguageAction({ ...languageAction, showAll: false });
                             setShowLanguageSelector(true);
                           }, 100);
                         }}
                       >
                         ⬅️ Back to project languages
                       </div>
                     )}
                   </>
                 );
               }
            })()}
            
            <ModalActions>
              <ActionButton onClick={() => setShowLanguageSelector(false)}>
                Cancel
              </ActionButton>
            </ModalActions>
          </LanguageSelectorContent>
        </LanguageSelectorModal>
      )}



      {/* Format Selector Modal */}
      {showFormatSelector && (
        <FormatSelectorModal onClick={() => setShowFormatSelector(false)}>
          <FormatSelectorContent onClick={(e) => e.stopPropagation()}>
            <FormatSelectorTitle>
              📐 Select format for {formatAction?.data?.name || formatAction?.type}
            </FormatSelectorTitle>
            
            {(() => {
              // Get saved presets from localStorage
              const savedPresets = (() => {
                try {
                  return JSON.parse(localStorage.getItem('formatPresets') || '[]');
                } catch {
                  return [];
                }
              })();

              const defaultFormats = [
                {
                  id: '16:9',
                  name: 'YouTube',
                  ratio: '16/9',
                  description: 'Landscape (1920x1080)'
                },
                {
                  id: '9:16',
                  name: 'Shorts',
                  ratio: '9/16',
                  description: 'Portrait (1080x1920)'
                },
                {
                  id: '1:1',
                  name: 'Instagram',
                  ratio: '1/1',
                  description: 'Square (1080x1080)'
                },
                {
                  id: '4:5',
                  name: 'Instagram',
                  ratio: '4/5',
                  description: 'Portrait (1080x1350)'
                }
              ];

              // Get currently selected formats for this group
              const getCurrentFormats = () => {
                if (formatAction?.type === 'group') {
                  const groupData = formatAction.data;
                  if (groupData.id === 'individual-parts') {
                    // Check if individual parts have multiple formats
                    const formats = new Set();
                    parts.forEach(part => {
                      if (part.formats && part.formats.length > 0) {
                        part.formats.forEach(f => formats.add(f));
                      } else if (part.format) {
                        formats.add(part.format);
                      }
                    });
                    return Array.from(formats);
                  } else {
                    // Check group formats
                    return groupData.formats || (groupData.format ? [groupData.format] : []);
                  }
                }
                return [];
              };

              const currentFormats = getCurrentFormats();

              const isFormatSelected = (formatId) => {
                return selectedMultiFormats.some(f => f.id === formatId);
              };

              const handleMultiSelectToggle = (format) => {
                const isSelected = selectedMultiFormats.some(f => f.id === format.id);
                if (isSelected) {
                  setSelectedMultiFormats(prev => prev.filter(f => f.id !== format.id));
                } else {
                  setSelectedMultiFormats(prev => [...prev, format]);
                }
              };

              return (
                <>
                  {/* Format Selection Header */}
                  <div style={{ 
                    padding: '8px 12px', 
                    fontSize: '11px', 
                    color: '#666',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    textAlign: 'center'
                  }}>
                    📐 Select Formats (Choose one or more)
                  </div>
                  
                  {defaultFormats.map(format => {
                    const isSelected = selectedMultiFormats.some(f => f.id === format.id);
                    
                    return (
                      <FormatOption 
                        key={format.id} 
                        onClick={() => handleMultiSelectToggle(format)}
                        style={{
                          border: `2px solid ${isSelected ? '#ff6b35' : '#dee2e6'}`,
                          backgroundColor: isSelected ? '#ff6b3510' : '#fff',
                          cursor: 'pointer'
                        }}
                      >
                        <FormatIcon ratio={format.ratio}>
                          Preview
                        </FormatIcon>
                        <FormatDetails>
                          <FormatName style={{ 
                            color: isSelected ? '#ff6b35' : '#333'
                          }}>
                            {format.name} 
                            {isSelected && (
                              <span style={{ marginLeft: '8px', color: '#ff6b35', fontWeight: 'bold' }}>✓</span>
                            )}
                          </FormatName>
                          <FormatDescription>{format.description} • {format.ratio}</FormatDescription>
                        </FormatDetails>
                      </FormatOption>
                    );
                   })}

                  {/* Format Selection Info */}
                  {selectedMultiFormats.length > 0 && (
                    <div style={{ 
                      padding: '8px 12px',
                      backgroundColor: '#f0f8ff',
                      borderRadius: '4px',
                      margin: '8px 0',
                      fontSize: '11px',
                      color: '#666',
                      border: '1px solid #e3f2fd'
                    }}>
                      <strong style={{ color: '#ff6b35' }}>
                        Selected: {selectedMultiFormats.length} format{selectedMultiFormats.length > 1 ? 's' : ''}
                      </strong>
                      <div style={{ marginTop: '4px' }}>
                        {selectedMultiFormats.map(f => `${f.name} (${f.ratio})`).join(' • ')}
                      </div>
                    </div>
                  )}

                  {/* Saved presets */}
                  {savedPresets.length > 0 && (
                    <>
                      <div style={{ 
                        padding: '8px 12px', 
                        fontSize: '11px', 
                        color: '#666',
                        backgroundColor: '#e7f3ff',
                        borderRadius: '4px',
                        margin: '16px 0 8px 0',
                        textAlign: 'center'
                      }}>
                        💾 Saved Presets
                      </div>
                      
                      {savedPresets.slice(0, 5).map(preset => {
                        const presetFormat = {
                          id: preset.id, // ✅ Fixed: Use original ID without prefix
                          name: preset.name,
                          ratio: preset.aspectRatio,
                          customization: preset.customization,
                          isPreset: true,
                          originalFormat: preset.format // Keep original format for backend
                        };
                        const isSelected = selectedMultiFormats.some(f => f.id === presetFormat.id);
                        
                        console.log('🔍 Preset format check:', {
                          presetId: preset.id,
                          presetFormat: presetFormat,
                          isSelected: isSelected,
                          selectedIds: selectedMultiFormats.map(f => f.id)
                        });
                        
                        return (
                          <FormatOption 
                            key={preset.id} 
                            onClick={() => handleMultiSelectToggle(presetFormat)}
                            style={{
                              border: `2px solid ${isSelected ? '#ff6b35' : '#dee2e6'}`,
                              backgroundColor: isSelected ? '#ff6b3510' : '#fff',
                              cursor: 'pointer'
                            }}
                          >
                            <FormatIcon ratio={preset.aspectRatio}>
                              {preset.format === 'advanced' ? 'ADV' : 'Custom'}
                            </FormatIcon>
                            <FormatDetails>
                              <FormatName style={{ 
                                color: isSelected ? '#ff6b35' : '#333',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                {preset.name}
                                {preset.customization?.faceTrackingEnabled && (
                                  <span title="Face Tracking Enabled" style={{ fontSize: '14px' }}>🎯</span>
                                )}
                                {isSelected && (
                                  <span style={{ marginLeft: 'auto', color: '#ff6b35', fontWeight: 'bold' }}>✓</span>
                                )}
                              </FormatName>
                              <FormatDescription>
                                {preset.format === 'custom' 
                                  ? `Custom ${preset.customWidth}:${preset.customHeight}` 
                                  : preset.format === 'advanced'
                                  ? 'Advanced Customization'
                                  : preset.format.toUpperCase()
                                }
                              </FormatDescription>
                            </FormatDetails>
                          </FormatOption>
                        );
                      })}
                      
                      {savedPresets.length > 5 && (
                        <div style={{ 
                          padding: '8px 12px', 
                          fontSize: '10px', 
                          color: '#666',
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}>
                          +{savedPresets.length - 5} more presets available in Format & Cropping page
                        </div>
                      )}
                    </>
                  )}

                  <div style={{ 
                    padding: '8px 12px', 
                    fontSize: '10px', 
                    color: '#007bff',
                    textAlign: 'center',
                    borderTop: '1px solid #eee',
                    marginTop: '16px',
                    cursor: 'pointer',
                    borderRadius: '4px'
                  }}
                  onClick={() => {
                    setShowFormatSelector(false);
                    // You could add navigation to Format & Cropping page here
                    alert('💡 Tip: Go to Format & Cropping page to create custom formats and advanced customization!');
                  }}
                  >
                    ⚙️ Create custom formats in Format & Cropping page
                  </div>


                </>
              );
            })()}
            
            <ModalActions>
              <ActionButton onClick={() => {
                setShowFormatSelector(false);
                setSelectedMultiFormats([]);
              }}>
                Cancel
              </ActionButton>
              <ActionButton 
                onClick={() => {
                  if (selectedMultiFormats.length > 0) {
                    handleMultiFormatSelection();
                  } else {
                    // Clear formats if nothing selected
                    if (formatAction.type === 'group') {
                      const groupData = formatAction.data;
                      
                      if (groupData.id === 'individual-parts') {
                        // Clear formats from individual parts
                        const updatedParts = parts.map(part => ({
                          ...part,
                          format: null,
                          aspectRatio: null,
                          formats: null,
                          customization: null,
                          presetName: null,
                          presetNames: null,
                          formatNames: null
                        }));
                        onUpdate({ parts: updatedParts });
                      } else {
                        // Clear formats from group
                        const updatedGroups = groups.map(group => {
                          if (group.id === groupData.id) {
                            return {
                              ...group,
                              format: null,
                              aspectRatio: null,
                              formats: null,
                              customization: null,
                              presetName: null,
                              presetNames: null,
                              formatNames: null
                            };
                          }
                          return group;
                        });
                        onUpdate({ groups: updatedGroups });
                      }
                    }
                    setShowFormatSelector(false);
                    setFormatAction(null);
                    setSelectedMultiFormats([]);
                  }
                }}
                style={{ 
                  backgroundColor: selectedMultiFormats.length > 0 ? '#ff6b35' : '#6c757d', 
                  color: 'white',
                  fontWeight: '600'
                }}
              >
                {selectedMultiFormats.length > 0 ? 
                  `✅ Save ${selectedMultiFormats.length} Format${selectedMultiFormats.length > 1 ? 's' : ''}` :
                  '🗑️ Clear Formats'
                }
              </ActionButton>
            </ModalActions>
          </FormatSelectorContent>
        </FormatSelectorModal>
      )}

      {/* Watermark Selector Modal */}
      {showWatermarkSelector && (
        <WatermarkSelectorModal onClick={() => setShowWatermarkSelector(false)}>
          <WatermarkSelectorContent onClick={(e) => e.stopPropagation()}>
            <WatermarkSelectorTitle>
              💧 Select watermark for {watermarkAction?.data?.name || watermarkAction?.type}
            </WatermarkSelectorTitle>
            
            {(() => {
              // Get saved watermark presets from localStorage
              const savedWatermarkPresets = (() => {
                try {
                  return JSON.parse(localStorage.getItem('watermarkPresets') || '[]');
                } catch {
                  return [];
                }
              })();

              const handleMultiWatermarkToggle = (watermark) => {
                const isSelected = selectedMultiWatermarks.some(w => w.id === watermark.id);
                if (isSelected) {
                  setSelectedMultiWatermarks(prev => prev.filter(w => w.id !== watermark.id));
                } else {
                  setSelectedMultiWatermarks(prev => [...prev, watermark]);
                }
              };

              return (
                <>
                  {/* Saved watermark presets */}
                  {savedWatermarkPresets.length > 0 ? (
                    <>
                      <div style={{ 
                        padding: '8px 12px', 
                        fontSize: '11px', 
                        color: '#666',
                        backgroundColor: '#e7f3ff',
                        borderRadius: '4px',
                        margin: '0 0 8px 0',
                        textAlign: 'center'
                      }}>
                        💾 Saved Watermark Presets
                      </div>
                      
                      {savedWatermarkPresets.slice(0, 10).map(preset => {
                        const presetWatermark = {
                          id: preset.id, 
                          name: preset.name,
                          settings: preset.settings,
                          isPreset: true
                        };
                        const isSelected = selectedMultiWatermarks.some(w => w.id === presetWatermark.id);
                        
                        return (
                          <WatermarkOption 
                            key={preset.id} 
                            onClick={() => handleMultiWatermarkToggle(presetWatermark)}
                            style={{
                              border: `2px solid ${isSelected ? '#007bff' : '#dee2e6'}`,
                              backgroundColor: isSelected ? '#007bff10' : '#fff',
                              cursor: 'pointer'
                            }}
                          >
                            <WatermarkIcon>
                              💎
                            </WatermarkIcon>
                            <WatermarkDetails>
                              <WatermarkName style={{ 
                                color: isSelected ? '#007bff' : '#333'
                              }}>
                                {preset.name}
                                {isSelected && (
                                  <span style={{ marginLeft: '8px', color: '#007bff', fontWeight: 'bold' }}>✓</span>
                                )}
                              </WatermarkName>
                              <WatermarkDescription>
                                {preset.settings?.type === 'text' ? 'Custom Text Watermark' : 'Custom Image Watermark'}
                              </WatermarkDescription>
                            </WatermarkDetails>
                          </WatermarkOption>
                        );
                      })}
                      
                      {savedWatermarkPresets.length > 10 && (
                        <div style={{ 
                          padding: '8px 12px', 
                          fontSize: '10px', 
                          color: '#666',
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}>
                          +{savedWatermarkPresets.length - 10} more watermark presets available in Watermark Settings page
                        </div>
                      )}

                      {/* Watermark Selection Info */}
                      {selectedMultiWatermarks.length > 0 && (
                        <div style={{ 
                          padding: '8px 12px',
                          backgroundColor: '#f0f8ff',
                          borderRadius: '4px',
                          margin: '8px 0',
                          fontSize: '11px',
                          color: '#666',
                          border: '1px solid #e3f2fd'
                        }}>
                          <strong style={{ color: '#007bff' }}>
                            Selected: {selectedMultiWatermarks.length} watermark{selectedMultiWatermarks.length > 1 ? 's' : ''}
                          </strong>
                          <div style={{ marginTop: '4px' }}>
                            {selectedMultiWatermarks.map(w => w.name).join(' • ')}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ 
                      padding: '20px', 
                      textAlign: 'center',
                      color: '#666',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>💾</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                        No Saved Watermark Presets
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        Create custom watermarks in Watermark Settings page first
                      </div>
                    </div>
                  )}

                  <div style={{ 
                    padding: '8px 12px', 
                    fontSize: '10px', 
                    color: '#007bff',
                    textAlign: 'center',
                    borderTop: '1px solid #eee',
                    marginTop: '16px',
                    cursor: 'pointer',
                    borderRadius: '4px'
                  }}
                  onClick={() => {
                    setShowWatermarkSelector(false);
                    alert('💡 Tip: Go to Watermark Settings page to create custom watermarks and save presets!');
                  }}
                  >
                    ⚙️ Create custom watermarks in Watermark Settings page
                  </div>

                </>
              );
            })()}
            
            <ModalActions>
              <ActionButton onClick={() => setShowWatermarkSelector(false)}>
                Cancel
              </ActionButton>
              {selectedMultiWatermarks.length > 0 && (
                <ActionButton 
                  onClick={() => {
                    setSelectedMultiWatermarks([]);
                    console.log('🧹 Cleared watermark selection');
                  }}
                  style={{
                    background: '#6c757d',
                    color: 'white',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  🧹 Clear Selection
                </ActionButton>
              )}
              <ActionButton 
                onClick={handleMultiWatermarkSelection}
                style={{
                  background: selectedMultiWatermarks.length > 0 ? '#007bff' : '#dc3545',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {selectedMultiWatermarks.length > 0 ? 
                  `✅ Apply ${selectedMultiWatermarks.length} Watermark${selectedMultiWatermarks.length > 1 ? 's' : ''}` :
                  '🗑️ Remove All Watermarks'
                }
              </ActionButton>
            </ModalActions>
          </WatermarkSelectorContent>
        </WatermarkSelectorModal>
      )}

      {/* Subtitle Selector Modal */}
      {isSubtitleSelectorOpen && (
        <SubtitleSelectorModal onClick={() => setIsSubtitleSelectorOpen(false)}>
          <SubtitleSelectorContent onClick={(e) => e.stopPropagation()}>
            <SubtitleSelectorTitle>
              💬 Select subtitle preset for {selectedTarget?.data?.name || selectedTarget?.type}
            </SubtitleSelectorTitle>
            
            {(() => {
              // Get saved subtitle presets from localStorage
              const savedSubtitlePresets = getSavedSubtitlePresets();

              const handleMultiSubtitleToggle = (subtitle) => {
                const isSelected = selectedSubtitlePresets.some(s => s.id === subtitle.id);
                if (isSelected) {
                  setSelectedSubtitlePresets(prev => prev.filter(s => s.id !== subtitle.id));
                } else {
                  setSelectedSubtitlePresets(prev => [...prev, subtitle]);
                }
              };

              return (
                <>
                  {/* Saved subtitle presets */}
                  {savedSubtitlePresets.length > 0 ? (
                    <>
                      <div style={{ 
                        padding: '8px 12px', 
                        fontSize: '11px', 
                        color: '#666',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        margin: '0 0 8px 0',
                        textAlign: 'center'
                      }}>
                        🎬 Saved Subtitle Presets
                      </div>
                      
                      {savedSubtitlePresets.slice(0, 10).map(preset => {
                        const presetSubtitle = {
                          id: preset.id, // ✅ Fixed: Use original ID without prefix
                          name: preset.name,
                          settings: preset.settings,
                          isPreset: true
                        };
                        const isSelected = selectedSubtitlePresets.some(s => s.id === presetSubtitle.id);
                        
                        return (
                          <SubtitlePresetOption 
                            key={preset.id} 
                            onClick={() => handleMultiSubtitleToggle(presetSubtitle)}
                            style={{
                              border: `2px solid ${isSelected ? '#007bff' : '#dee2e6'}`,
                              backgroundColor: isSelected ? '#007bff10' : '#fff',
                              cursor: 'pointer'
                            }}
                          >
                            <SubtitlePresetIcon>
                              🎬
                            </SubtitlePresetIcon>
                            <SubtitlePresetDetails>
                              <SubtitlePresetName style={{ 
                                color: isSelected ? '#007bff' : '#333'
                              }}>
                                {preset.name}
                                {isSelected && (
                                  <span style={{ marginLeft: '8px', color: '#007bff', fontWeight: 'bold' }}>✓</span>
                                )}
                              </SubtitlePresetName>
                              <SubtitlePresetDescription>
                                Custom Subtitle Preset
                              </SubtitlePresetDescription>
                            </SubtitlePresetDetails>
                          </SubtitlePresetOption>
                        );
                      })}
                      
                      {savedSubtitlePresets.length > 10 && (
                        <div style={{ 
                          padding: '8px 12px', 
                          fontSize: '10px', 
                          color: '#666',
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}>
                          +{savedSubtitlePresets.length - 10} more subtitle presets available in Subtitle Settings page
                        </div>
                      )}

                      {/* Subtitle Selection Info */}
                      {selectedSubtitlePresets.length > 0 && (
                        <div style={{ 
                          padding: '8px 12px',
                          backgroundColor: '#f0f8ff',
                          borderRadius: '4px',
                          margin: '8px 0',
                          fontSize: '11px',
                          color: '#666',
                          border: '1px solid #e3f2fd'
                        }}>
                          <strong style={{ color: '#007bff' }}>
                            Selected: {selectedSubtitlePresets.length} subtitle preset{selectedSubtitlePresets.length > 1 ? 's' : ''}
                          </strong>
                          <div style={{ marginTop: '4px' }}>
                            {selectedSubtitlePresets.map(s => s.name).join(' • ')}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ 
                      padding: '20px', 
                      textAlign: 'center',
                      color: '#666',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>🎬</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                        No Saved Subtitle Presets
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        Create custom subtitle presets in Subtitle Settings page first
                      </div>
                    </div>
                  )}

                  <div style={{ 
                    padding: '8px 12px', 
                    fontSize: '10px', 
                    color: '#007bff',
                    textAlign: 'center',
                    borderTop: '1px solid #eee',
                    marginTop: '16px',
                    cursor: 'pointer',
                    borderRadius: '4px'
                  }}
                  onClick={() => {
                    setIsSubtitleSelectorOpen(false);
                    alert('💡 Tip: Go to Subtitle Settings page to create custom subtitle presets and save them!');
                  }}
                  >
                    ⚙️ Create custom subtitle presets in Subtitle Settings page
                  </div>

                </>
              );
            })()}
            
            <ModalActions>
              <ActionButton onClick={() => setIsSubtitleSelectorOpen(false)}>
                Cancel
              </ActionButton>
              <ActionButton 
                onClick={handleMultiSubtitleSelection}
                style={{
                  background: selectedSubtitlePresets.length > 0 ? '#007bff' : '#dc3545',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {selectedSubtitlePresets.length > 0 ? 
                  `✅ Apply ${selectedSubtitlePresets.length} Subtitle Preset${selectedSubtitlePresets.length > 1 ? 's' : ''}` :
                  '🗑️ Remove All Subtitles'
                }
              </ActionButton>
            </ModalActions>
          </SubtitleSelectorContent>
        </SubtitleSelectorModal>
      )}

      {backupStatus && (
        <DataRecoveryIndicator type={backupStatus.type}>
          <span>💾 {backupStatus.message}</span>
          {backupStatus.action && (
            <ActionButton 
              onClick={backupStatus.action}
              style={{ fontSize: '10px', padding: '4px 8px', marginLeft: 'auto' }}
            >
              Restore Now
            </ActionButton>
          )}
        </DataRecoveryIndicator>
      )}
      
      <Header>
        <Title>
          <Scissors size={16} />
          ✂️ Cut
        </Title>
        <Actions>
          {(groups.length > 0) && (
            <ActionButton onClick={() => handleExportAllPartsClick(groups.flatMap(g => [...(g.segments || []), ...(g.parts || [])]))}>
              <Download />
              Export All ({(() => {
                // Calculate total parts to be exported (ONLY GROUPS, NO INDIVIDUAL PARTS)
                let totalParts = 0;
                
                // 🚫 REMOVED: Don't count individual parts (functionality removed)
                // totalParts += individualParts.length;
                
                // Count all parts in groups (segments + parts)
                groups.forEach(group => {
                  totalParts += (group.segments?.length || 0); // Each segment = 1 part
                  totalParts += (group.parts?.length || 0);    // Each part = 1 part
                });
                
                return totalParts;
              })()} parts)
            </ActionButton>
          )}
          {(() => {
            // 🔒 DISABLED: const restored = restorePartsAndGroups(project.id);
    console.log('🔒 DISABLED backup restore to prevent subtitle preset conflicts');
    const restored = { parts: [], groups: [], individualPartsLanguage: null };
            const hasRealBackupData = (restored.parts.length > 0) || (restored.groups.length > 0) || restored.individualPartsLanguage;
            
            if (hasRealBackupData) {
              return (
                <ActionButton 
                  onClick={() => {
                    const choice = window.confirm(`💾 Restore from latest backup?\n\n📊 Current data:\n• ${parts.length} parts\n• ${groups.length} groups\n\n💾 Backup data:\n• ${restored.parts.length} parts\n• ${restored.groups.length} groups\n${restored.individualPartsLanguage ? `• Individual Parts Language: ${restored.individualPartsLanguage}` : ''}\n\n⚠️ This will replace your current data!`);
                    
                    if (choice) {
                      onUpdate({ 
                        parts: restored.parts, 
                        groups: restored.groups 
                      });
                      
                      // Restore individual parts language
                      if (restored.individualPartsLanguage) {
                        setIndividualPartsLanguage(restored.individualPartsLanguage);
                      }
                      
                      setBackupStatus({
                        type: 'info',
                        message: `✅ Restored from backup successfully!`,
                        action: null
                      });
                      
                      // Hide message after 3 seconds
                      setTimeout(() => {
                        setBackupStatus(null);
                      }, 3000);
                    }
                  }}
                  title="Restore from latest backup"
                >
                  💾 Restore
                </ActionButton>
              );
            }
            return null; // Don't show restore button if no real backup data
          })()}
          
          {/* Clear Backup Button - show if ANY backup exists (even empty) */}
          {(() => {
            try {
              const backups = JSON.parse(localStorage.getItem(BACKUP_KEY) || '{}');
              const hasAnyBackup = !!backups[project.id];
              
              if (hasAnyBackup) {
                return (
                  <ActionButton 
                    onClick={() => {
                      const confirmed = window.confirm('🗑️ Clear all backup data for this project?\n\nThis action cannot be undone.');
                      if (confirmed) {
                        const success = clearBackupsForProject(project.id);
                        if (success) {
                          alert('✅ Backup data cleared successfully!');
                          // Force re-render by updating state
                          setBackupStatus({
                            type: 'info',
                            message: '🗑️ Backup data cleared',
                            action: null
                          });
                          setTimeout(() => setBackupStatus(null), 2000);
                        } else {
                          alert('❌ Failed to clear backup data');
                        }
                      }
                    }}
                    title="Clear backup data"
                    style={{ background: '#dc3545', color: 'white' }}
                  >
                    🗑️ Clear
                  </ActionButton>
                );
              }
            } catch (error) {
              console.error('Error checking backups:', error);
            }
            return null;
          })()}
          
          {/* Debug button - temporary */}
          <ActionButton 
            onClick={() => {
              debugAllBackups();
              console.log('🔍 Current project:', {
                id: project.id,
                parts: parts.length,
                groups: groups.length,
                individualPartsLanguage: individualPartsLanguage
              });
            }}
            title="Debug backups (check console)"
            style={{ background: '#17a2b8', color: 'white', fontSize: '10px', padding: '4px 8px' }}
          >
            🔍 Debug
          </ActionButton>
        </Actions>
      </Header>

      {/* Individual Parts Group - Show as first group */}
      {false && individualParts.length > 0 && (
      <CutterSection>
          <CollapsibleGroupTitle onClick={() => handleGroupExpansion('individual-parts')}>
            <GroupTitleLeft>
              <FileText size={14} />
              📄 Individual Parts ({individualParts.length} parts)
              {individualPartsLanguage && (
                <LanguageBadge
                  color="#007bff20"
                  textColor="#007bff"
                  borderColor="#007bff40"
                  style={{ marginLeft: '8px' }}
                >
                  {getLanguageInfo(individualPartsLanguage).flag} {getLanguageInfo(individualPartsLanguage).name}
                </LanguageBadge>
              )}
              {(() => {
                // Get all unique formats from individual parts
                const allFormats = new Set();
                const allFormatNames = new Set();
                individualParts.forEach(part => {
                  if (part.formats && part.formats.length > 0) {
                    part.formats.forEach(f => allFormats.add(f));
                  } else if (part.format) {
                    allFormats.add(part.format);
                  }
                  // Collect format names (includes both presets and default format names)
                  if (part.formatNames && Array.isArray(part.formatNames)) {
                    part.formatNames.forEach(name => allFormatNames.add(name));
                  } else {
                    // Fallback to preset names if formatNames not available
                    if (part.presetName) {
                      allFormatNames.add(part.presetName);
                    }
                    if (part.presetNames && Array.isArray(part.presetNames)) {
                      part.presetNames.forEach(name => allFormatNames.add(name));
                    }
                  }
                });
                
                if (allFormats.size > 0) {
                  let displayText = '';
                  
                  if (allFormatNames.size > 0) {
                    // Show format names (includes both presets and default formats)
                    const formatList = Array.from(allFormatNames);
                    if (formatList.length === 1) {
                      displayText = formatList[0];
                    } else if (formatList.length <= 3) {
                      displayText = formatList.join('+');
                    } else {
                      displayText = `${formatList.slice(0, 2).join('+')}+${formatList.length - 2} more`;
                    }
                  } else {
                    // Fall back to format mapping
                    const formatMap = {
                      '16:9': 'YT',
                      '9:16': 'Shorts',
                      '1:1': 'IG',
                      '4:5': 'IGP'
                    };
                    displayText = Array.from(allFormats).map(f => formatMap[f] || f).join('+');
                  }
                  
                  return (
                    <LanguageBadge
                      color="#4285f420"
                      textColor="#4285f4"
                      borderColor="#4285f440"
                      style={{ marginLeft: '8px' }}
                    >
                      📐 {displayText} ({allFormats.size})
                    </LanguageBadge>
                  );
                }
                return null;
              })()}
              {(() => {
                // Get all unique watermarks from individual parts
                const allWatermarks = new Set();
                const allWatermarkNames = new Set();
                individualParts.forEach(part => {
                  if (part.watermarks && part.watermarks.length > 0) {
                    part.watermarks.forEach(w => allWatermarks.add(w));
                  } else if (part.watermark) {
                    allWatermarks.add(part.watermark);
                  }
                  // Collect watermark names (includes both presets and default watermark names)
                  if (part.watermarkNames && Array.isArray(part.watermarkNames)) {
                    part.watermarkNames.forEach(name => allWatermarkNames.add(name));
                  } else {
                    // Fallback to preset names if watermarkNames not available
                    if (part.watermarkPresetName) {
                      allWatermarkNames.add(part.watermarkPresetName);
                    }
                    if (part.watermarkPresetNames && Array.isArray(part.watermarkPresetNames)) {
                      part.watermarkPresetNames.forEach(name => allWatermarkNames.add(name));
                    }
                  }
                });
                
                if (allWatermarks.size > 0) {
                  let displayText = '';
                  
                  if (allWatermarkNames.size > 0) {
                    // Show watermark names (includes both presets and default watermarks)
                    const watermarkList = Array.from(allWatermarkNames);
                    if (watermarkList.length === 1) {
                      displayText = watermarkList[0];
                    } else if (watermarkList.length <= 3) {
                      displayText = watermarkList.join('+');
                    } else {
                      displayText = `${watermarkList.slice(0, 2).join('+')}+${watermarkList.length - 2} more`;
                    }
                  } else {
                    // Fall back to watermark mapping OR resolve ID to name
                    const watermarkMap = {
                      'none': 'None',
                      'text-basic': 'Text',
                      'text-branded': 'Branded',
                      'logo-basic': 'Logo',
                      'logo-branded': 'BLogo'
                    };
                    const displayNames = Array.from(allWatermarks).map(w => watermarkMap[w] || getWatermarkDisplayName(w));
                    displayText = displayNames.join('+');
                  }
                  
                  return (
                    <LanguageBadge
                      color="#17a2b820"
                      textColor="#17a2b8"
                      borderColor="#17a2b840"
                      style={{ marginLeft: '8px' }}
                    >
                      💧 {displayText} ({allWatermarks.size})
                    </LanguageBadge>
                  );
                }
                return null;
              })()}
            </GroupTitleLeft>
            <ExpandIcon>
              {expandedGroups['individual-parts'] !== false ? <ChevronDown /> : <ChevronUp />}
            </ExpandIcon>
          </CollapsibleGroupTitle>
          
          {expandedGroups['individual-parts'] !== false && (
            <GroupContent>
              {/* Bulk Operations for Individual Parts */}
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                marginBottom: '12px',
                flexWrap: 'wrap'
              }}>
                <ActionButton 
                  onClick={() => handleExportAllPartsClick(individualParts)}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  <Download size={12} />
                  Export All ({individualParts.length} parts)
                </ActionButton>
                <ActionButton 
                  onClick={() => {
                    if (individualParts.length === 0) return;
                    const firstPart = individualParts[0];
                    if (onSeek) {
                      onSeek(firstPart.startTime);
                      if (onPlayPause) {
                        onPlayPause(true);
                      }
                      setCurrentPlayingPart({
                        startTime: firstPart.startTime,
                        endTime: firstPart.endTime,
                        name: firstPart.name || firstPart.text?.substring(0, 30) || 'Individual Part'
                      });
                    }
                  }}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  <Play size={12} />
                  Play All
                </ActionButton>
                <ActionButton 
                  onClick={() => openLanguageSelector('group', { id: 'individual-parts', name: 'Individual Parts' })}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  🌐 Language
                </ActionButton>
                <ActionButton 
                  onClick={() => openFormatSelector('group', { id: 'individual-parts', name: 'Individual Parts' })}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  📐 Format
                </ActionButton>
                <ActionButton 
                  onClick={() => openWatermarkSelector('group', { id: 'individual-parts', name: 'Individual Parts' })}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  💧 Watermark
                </ActionButton>
                <ActionButton 
                  onClick={() => openSubtitleSelector('group', { id: 'individual-parts', name: 'Individual Parts' })}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  🎬 Subtitle
                </ActionButton>

                <ActionButton 
                  className="danger"
                  onClick={handleDeleteAllIndividualParts}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  <Trash2 size={12} />
                  Delete All
                </ActionButton>
              </div>

              <PartsList>
                {individualParts.map((part) => (
                  <PartCard 
                    key={part.id} 
                    style={{ 
                      borderLeft: '3px solid #007bff'
                    }}
                    onClick={() => {
                      console.log('📍 Individual part clicked for seeking:', part.startTime);
                      handleSeekOnlyToPart(part);
                    }}
                  >
                    <PartHeader>
                      <PartName>
                        📄 Part {part.id} • {formatTime(part.startTime)} - {formatTime(part.endTime)}
                      </PartName>
                      <PartActions>
                        <PartActionButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('▶️ Individual part play button clicked:', part.startTime);
                            handleSeekToPart(part);
                          }}
                          title={isPlaying && Math.abs(currentTime - part.startTime) < 1 ? "Currently playing here" : "Play from this time"}
                        >
                          {isPlaying && Math.abs(currentTime - part.startTime) < 1 ? <Pause /> : <Play />}
                        </PartActionButton>
                        
                        {/* Show split button for multi-segment parts */}
                        {(part.isMultiSegmentSelection || part.originalSegments) && (
                          <PartActionButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSplitPart(part);
                            }}
                            title={`Split into ${part.originalSegments?.length || 'individual'} segments`}
                            style={{ color: '#ff9500' }}
                          >
                            <Scissors />
                          </PartActionButton>
                        )}
                        
                        <PartActionButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportPartClick(part);
                          }}
                          title="Export this part"
                        >
                          <Download />
                        </PartActionButton>
                        
                        {/* Move part button */}
                        <PartActionButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            showMoveToGroupSelector('part', part);
                          }}
                          title="Move to group"
                          style={{ color: '#007bff' }}
                        >
                          📁
                        </PartActionButton>
                        
                        <PartActionButton 
                          className="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePart(part.id);
                          }}
                          title="Delete this part"
                        >
                          <Trash2 />
                        </PartActionButton>
                      </PartActions>
                    </PartHeader>
                    
                    <PartInfo>
                      <PartDuration>
                        Duration: {formatTime(part.duration || (part.endTime - part.startTime))}
                      </PartDuration>
                      <PartTimeRange>
                        {formatTime(part.startTime)} - {formatTime(part.endTime)}
                      </PartTimeRange>
                      {part.speaker && (
                        <span style={{ 
                          fontSize: '10px', 
                          padding: '2px 6px', 
                          backgroundColor: '#007bff20', 
                          borderRadius: '8px',
                          color: '#007bff'
                        }}>
                          {part.speaker}
                        </span>
                      )}
                      
                      {/* Show current language or original part language */}
                      {(individualPartsLanguage || part.language) && (
                        <LanguageBadge
                          color="#007bff20"
                          textColor="#007bff"
                          borderColor="#007bff40"
                        >
                          {/* Prioritize selected language over original part language */}
                          {individualPartsLanguage ? (
                            <>
                              {getLanguageInfo(individualPartsLanguage).flag} {getLanguageInfo(individualPartsLanguage).name}
                              {individualPartsLanguage !== 'original' && part.language && part.language !== individualPartsLanguage && (
                                <span style={{ fontSize: '8px', opacity: 0.7 }}>
                                  {' '}(was {getLanguageInfo(part.language).flag})
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              {getLanguageInfo(part.language).flag} {getLanguageInfo(part.language).name}
                            </>
                          )}
                        </LanguageBadge>
                      )}
                      
                      {/* Add watermark badge for individual parts */}
                      {(part.watermark || part.watermarkSettings || part.watermarks) && (() => {
                        const allWatermarks = new Set();
                        const allWatermarkNames = new Set();
                        
                        // Only show watermark badge if watermark is actually applied (not just stored data)
                        if (part.watermarks && part.watermarks.length > 0) {
                          part.watermarks.forEach(w => {
                            if (w && w !== 'none' && w !== '') {
                              allWatermarks.add(w);
                            }
                          });
                        } else if (part.watermark && part.watermark !== 'none' && part.watermark !== '') {
                          allWatermarks.add(part.watermark);
                        }
                        
                        if (part.watermarkNames && part.watermarkNames.length > 0) {
                          part.watermarkNames.forEach(name => {
                            if (name && name !== 'none' && name !== '') {
                              allWatermarkNames.add(name);
                            }
                          });
                        }
                        
                        // Only show badge if there are actual watermarks (not 'none' or empty)
                        if (allWatermarks.size > 0) {
                          let displayText = '';
                          
                          if (allWatermarkNames.size > 0) {
                            // Show watermark names (includes both presets and default watermarks)
                            const watermarkList = Array.from(allWatermarkNames);
                            if (watermarkList.length === 1) {
                              displayText = watermarkList[0];
                            } else if (watermarkList.length <= 3) {
                              displayText = watermarkList.join('+');
                            } else {
                              displayText = `${watermarkList.slice(0, 2).join('+')}+${watermarkList.length - 2} more`;
                            }
                          } else if (part.watermarkPresetName && part.watermarkPresetName !== 'none') {
                            // Show preset name if available
                            displayText = part.watermarkPresetName;
                          } else {
                            // Fall back to watermark mapping
                            const watermarkMap = {
                              'text-basic': 'Text',
                              'text-branded': 'Branded',
                              'logo-basic': 'Logo',
                              'logo-branded': 'BLogo'
                            };
                            displayText = Array.from(allWatermarks).map(w => watermarkMap[w] || w).join('+');
                          }
                          
                          // Only render if displayText is not empty
                          if (displayText && displayText !== '' && displayText !== 'None') {
                            return (
                              <LanguageBadge
                                color="#17a2b820"
                                textColor="#17a2b8"
                                borderColor="#17a2b840"
                              >
                                💧 {displayText}
                              </LanguageBadge>
                            );
                          }
                        }
                        return null;
                      })()}

                      {/* Add subtitle badge for individual parts */}
                      {(part.subtitlePresets || part.subtitlePresetName || part.subtitlePresetNames) && (() => {
                        let displayText = '';
                        
                        if (part.subtitlePresetNames && part.subtitlePresetNames.length > 0) {
                          // Show subtitle preset names
                          const nameList = part.subtitlePresetNames.filter(name => name && name !== 'None');
                          if (nameList.length === 1) {
                            displayText = nameList[0];
                          } else if (nameList.length <= 3) {
                            displayText = nameList.join('+');
                          } else {
                            displayText = `${nameList.slice(0, 2).join('+')}+${nameList.length - 2} more`;
                          }
                        } else if (part.subtitlePresetName && part.subtitlePresetName !== 'None') {
                          displayText = part.subtitlePresetName;
                        }
                        
                        // Only render if displayText is not empty
                        if (displayText && displayText !== '' && displayText !== 'None') {
                          return (
                            <LanguageBadge
                              color="#f093fb20"
                              textColor="#f5576c"
                              borderColor="#f093fb40"
                              style={{ fontSize: '8px', padding: '1px 4px' }}
                            >
                              🎬 {displayText}
                            </LanguageBadge>
                          );
                        }
                        return null;
                      })()}
                    </PartInfo>
                    
                    {(() => {
                      const displayText = getTranslatedPartText(part, individualPartsLanguage);
                      const isTranslated = individualPartsLanguage && individualPartsLanguage !== 'original';
                      
                      // Always show text section since title no longer contains text
                      return displayText && displayText !== 'Part' && (
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '12px', 
                        color: '#666', 
                        fontStyle: 'italic',
                        maxHeight: '60px',
                        overflow: 'hidden'
                      }}>
                          {/* Always show full text */}
                          <div style={{ marginBottom: isTranslated ? '4px' : '0' }}>
                            "{displayText}"
                          </div>
                          
                          {/* Language indicator for translations */}
                          {isTranslated && (
                            <div>
                              <span style={{
                                fontSize: '10px',
                                padding: '3px 6px',
                                backgroundColor: '#007bff',
                                borderRadius: '8px',
                                color: '#ffffff',
                                fontWeight: '600',
                                border: '1px solid #0056b3'
                              }}>
                                🌍 {getLanguageInfo(individualPartsLanguage).flag} {getLanguageInfo(individualPartsLanguage).name}
                              </span>
                      </div>
                    )}
                        </div>
                      );
                    })()}
                  </PartCard>
                ))}
              </PartsList>
            </GroupContent>
          )}
        </CutterSection>
      )}

      {/* Other Groups Section - Show each group separately */}
      {groups.map((group) => (
        <CutterSection key={group.id}>
          <CollapsibleGroupTitle onClick={() => handleGroupExpansion(group.id)}>
            <GroupTitleLeft>
              <Folder size={14} />
              📁 {group.name} ({(group.segments?.length || 0) + (group.parts?.length || 0)} parts)
              <div style={{ display: 'inline-flex', gap: '4px', marginLeft: '12px' }}>
                <PartActionButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    const newName = window.prompt('Rename group:', group.name);
                    if (newName && newName.trim() && newName.trim() !== group.name) {
                      const updatedGroups = groups.map(g =>
                        g.id === group.id ? { ...g, name: newName.trim() } : g
                      );
                      onUpdate({ groups: updatedGroups });
                    }
                  }}
                  title="Rename this group"
                  style={{ width: '24px', height: '24px', fontSize: '10px' }}
                >
                  ✏️
                </PartActionButton>
                <PartActionButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    const newGroup = {
                      ...JSON.parse(JSON.stringify(group)), // deep copy
                      id: Date.now().toString(),
                      name: `${group.name} (copy)`,
                    };
                    onUpdate({ groups: [...groups, newGroup] });
                  }}
                  title="Duplicate this group"
                  style={{ width: '24px', height: '24px', fontSize: '10px' }}
                >
                  📋
                </PartActionButton>
              </div>
              {group.language && (
                <LanguageBadge
                  color="#ff9500"
                  textColor="#ff6b35"
                  borderColor="#ff950040"
                  style={{ marginLeft: '8px' }}
                >
                  {getLanguageInfo(group.language).flag} {getLanguageInfo(group.language).name}
                </LanguageBadge>
              )}
              {(() => {
                // Get all unique formats from this group
                const allFormats = new Set();
                if (group.formats && group.formats.length > 0) {
                  group.formats.forEach(f => allFormats.add(f));
                } else if (group.format) {
                  allFormats.add(group.format);
                }
                
                if (allFormats.size > 0) {
                  let displayText = '';
                  
                  if (group.presetName) {
                    // Show preset name if available
                    displayText = group.presetName;
                  } else {
                    // Fall back to format mapping
                    const formatMap = {
                      '16:9': 'YT',
                      '9:16': 'Shorts',
                      '1:1': 'IG',
                      '4:5': 'IGP'
                    };
                    displayText = Array.from(allFormats).map(f => formatMap[f] || f).join('+');
                  }
                  
                  return (
                    <LanguageBadge
                      color="#4285f420"
                      textColor="#4285f4"
                      borderColor="#4285f440"
                      style={{ marginLeft: '8px' }}
                    >
                      📐 {displayText} ({allFormats.size})
                    </LanguageBadge>
                  );
                }
                return null;
              })()}
              {(() => {
                // Watermark badge — supports: preset ID, watermarkNames array, watermarkSettings
                const wmNames = group.watermarkNames || [];
                const wmId    = group.watermark;
                const wmSettings = group.watermarkSettings;

                let displayText = '';

                if (wmNames.length > 0) {
                  // Preset ID(s) stored in wmNames
                  const valid = wmNames.filter(n => n && n !== 'none' && n !== 'None');
                  const displayNames = valid.map(n => getWatermarkDisplayName(n));
                  displayText = displayNames.length <= 2 ? displayNames.join('+') : `${displayNames.slice(0,2).join('+')}+${displayNames.length - 2} more`;
                } else if (group.watermarkPresetName) {
                  displayText = group.watermarkPresetName;
                } else if (wmSettings?.text) {
                  // Text watermark with no preset name
                  displayText = `"${wmSettings.text}"`;
                } else if (wmId && wmId !== 'none') {
                  // Fallback: show resolved name for the single ID
                  displayText = getWatermarkDisplayName(wmId);
                }

                if (!displayText) return null;

                return (
                  <LanguageBadge
                    color="#17a2b820"
                    textColor="#17a2b8"
                    borderColor="#17a2b840"
                    style={{ marginLeft: '8px' }}
                  >
                    💧 {displayText}
                  </LanguageBadge>
                );
              })()}

              {/* Add subtitle badge for groups */}
              {(group.subtitlePresets || group.subtitlePresetName || group.subtitlePresetNames) && (() => {
                let displayText = '';
                
                if (group.subtitlePresetNames && group.subtitlePresetNames.length > 0) {
                  // Show subtitle preset names
                  const nameList = group.subtitlePresetNames.filter(name => name && name !== 'None');
                  if (nameList.length === 1) {
                    displayText = nameList[0];
                  } else if (nameList.length <= 3) {
                    displayText = nameList.join('+');
                  } else {
                    displayText = `${nameList.slice(0, 2).join('+')}+${nameList.length - 2} more`;
                  }
                } else if (group.subtitlePresetName && group.subtitlePresetName !== 'None') {
                  displayText = group.subtitlePresetName;
                }
                
                // Only render if displayText is not empty
                if (displayText && displayText !== '' && displayText !== 'None') {
                  return (
                    <LanguageBadge
                      color="#f093fb20"
                      textColor="#f5576c"
                      borderColor="#f093fb40"
                      style={{ marginLeft: '8px' }}
                    >
                      🎬 {displayText}
                    </LanguageBadge>
                  );
                }
                return null;
              })()}
            </GroupTitleLeft>
            <ExpandIcon>
              {expandedGroups[group.id] ? <ChevronDown /> : <ChevronUp />}
            </ExpandIcon>
          </CollapsibleGroupTitle>
          
          {expandedGroups[group.id] && (
            <GroupContent>
              {/* Bulk Operations for this group */}
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                marginBottom: '12px',
                flexWrap: 'wrap'
              }}>
                <ActionButton 
                  onClick={() => handleExportGroupClick(group)}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  <Download size={12} />
                  Export All ({(group.segments?.length || 0) + (group.parts?.length || 0)} parts)
                </ActionButton>
                <ActionButton 
                  onClick={() => handlePlayGroup(group)}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  <Play size={12} />
                  Play All
                </ActionButton>
                <ActionButton 
                  onClick={() => openLanguageSelector('group', group)}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  🌐 Language
                </ActionButton>
                <ActionButton 
                  onClick={() => openFormatSelector('group', group)}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  📐 Format
                </ActionButton>
                <ActionButton 
                  onClick={() => openWatermarkSelector('group', group)}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  💧 Watermark
                </ActionButton>
                <ActionButton 
                  onClick={() => openSubtitleSelector('group', group)}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  🎬 Subtitle
                </ActionButton>

                <ActionButton 
                  onClick={() => {
                    const newName = window.prompt('Rename group:', group.name);
                    if (newName && newName.trim() && newName.trim() !== group.name) {
                      const updatedGroups = groups.map(g =>
                        g.id === group.id ? { ...g, name: newName.trim() } : g
                      );
                      onUpdate({ groups: updatedGroups });
                    }
                  }}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                  title="Rename this group"
                >
                  ✏️ Rename
                </ActionButton>
                <ActionButton 
                  onClick={() => {
                    const newGroup = {
                      ...JSON.parse(JSON.stringify(group)), // deep copy
                      id: Date.now().toString(),
                      name: `${group.name} (copy)`,
                    };
                    onUpdate({ groups: [...groups, newGroup] });
                  }}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                  title="Duplicate this group"
                >
                  📋 Duplicate
                </ActionButton>
                <ActionButton 
                  className="danger"
                  onClick={() => handleDeleteGroup(group.id)}
                  style={{ fontSize: '10px', padding: '4px 8px' }}
                >
                  <Trash2 size={12} />
                  Delete Group
                </ActionButton>
              </div>

              <PartsList>
                {/* Render segments */}
                {(group.segments || []).map((segmentId) => {
                  const segment = transcript.find(t => t.id === segmentId);
                  if (!segment) return null;
                  
                  return (
                    <PartCard 
                      key={`segment-${segmentId}`} 
                      style={{ 
                        borderLeft: '3px solid #28a745'
                      }}
                      onClick={() => {
                        console.log('📍 Group segment clicked for seeking:', segment.startTime);
                        handleSeekOnlyToSegment(segment);
                      }}
                    >
                      <PartHeader>
                        <PartName>
                          📄 Segment {segment.id} • {formatTime(segment.startTime)} - {formatTime(segment.endTime)}
                        </PartName>
                        <PartActions>
                          <PartActionButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('▶️ Group segment play button clicked:', segment.startTime);
                              handleSeekToSegment(segment);
                            }}
                            title={isPlaying && Math.abs(currentTime - segment.startTime) < 1 ? "Currently playing here" : "Play from this time"}
                          >
                            {isPlaying && Math.abs(currentTime - segment.startTime) < 1 ? <Pause /> : <Play />}
                          </PartActionButton>
                          <PartActionButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportSegment(segment);
                            }}
                            title="Export this segment"
                          >
                            <Download />
                          </PartActionButton>
                          {/* Move segment button */}
                          <PartActionButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              showMoveToGroupSelector('segment', segment, group.id);
                            }}
                            title="Move segment"
                            style={{ color: '#007bff' }}
                          >
                            📁
                          </PartActionButton>

                          
                          <PartActionButton 
                            className="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFromGroup(group.id, segmentId);
                            }}
                            title="Remove from group"
                          >
                            <Trash2 />
                          </PartActionButton>
                        </PartActions>
                      </PartHeader>
                      
                      <PartInfo>
                        <PartDuration>
                          Duration: {formatTime(segment.endTime - segment.startTime)}
                        </PartDuration>
                        <PartTimeRange>
                          {formatTime(segment.startTime)} - {formatTime(segment.endTime)}
                        </PartTimeRange>
                        {segment.speaker && (
                          <span style={{ 
                            fontSize: '10px', 
                            padding: '2px 6px', 
                            backgroundColor: '#28a74520', 
                            borderRadius: '8px',
                            color: '#28a745'
                          }}>
                            {segment.speaker}
                          </span>
                        )}
                        <span style={{ 
                          fontSize: '8px', 
                          padding: '1px 4px', 
                          backgroundColor: '#28a74520', 
                          borderRadius: '6px',
                          color: '#28a745',
                          fontWeight: '600'
                        }}>
                          SEGMENT
                        </span>
                      </PartInfo>
                      
                      {(() => {
                        const displayText = getSegmentText(segment.id, group.language);
                        const isTranslated = group.language && group.language !== 'original';
                        
                        // Always show text section since title no longer contains text
                        return displayText && (
                        <div style={{ 
                          marginTop: '8px', 
                          fontSize: '12px', 
                          color: '#666', 
                          fontStyle: 'italic',
                          maxHeight: '60px',
                          overflow: 'hidden'
                        }}>
                            {/* Always show full text */}
                            <div style={{ marginBottom: isTranslated ? '4px' : '0' }}>
                              "{displayText}"
                            </div>
                            
                            {/* Language indicator for translations */}
                            {isTranslated && (
                              <div>
                                <span style={{
                                  fontSize: '10px',
                                  padding: '3px 6px',
                                  backgroundColor: '#007bff',
                                  borderRadius: '8px',
                                  color: '#ffffff',
                                  fontWeight: '600',
                                  border: '1px solid #0056b3'
                                }}>
                                  🌍 {getLanguageInfo(group.language).flag} {getLanguageInfo(group.language).name}
                                </span>
                        </div>
                      )}
                          </div>
                        );
                      })()}
                    </PartCard>
                  );
                })}

                {/* Render parts */}
                {(group.parts || []).map((part) => (
                  <PartCard 
                    key={`part-${part.id}`} 
                    style={{ 
                      borderLeft: '3px solid #007bff'
                    }}
                    onClick={() => {
                      console.log('📍 Group part clicked for seeking:', part.startTime);
                      handleSeekOnlyToPart(part);
                    }}
                  >
                    <PartHeader>
                      <PartName>
                        
                        📄 Part {part.id} • {formatTime(part.startTime)} - {formatTime(part.endTime)}
                      </PartName>
                      <PartActions>
                        <PartActionButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('▶️ Group part play button clicked:', part.startTime);
                            handleSeekToPart(part);
                          }}
                          title={isPlaying && Math.abs(currentTime - part.startTime) < 1 ? "Currently playing here" : "Play from this time"}
                        >
                          {isPlaying && Math.abs(currentTime - part.startTime) < 1 ? <Pause /> : <Play />}
                        </PartActionButton>

                        {/* Show split button for multi-segment parts */}
                        {(part.isMultiSegmentSelection || part.originalSegments) && (
                          <PartActionButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSplitPart(part);
                            }}
                            title={`Split into ${part.originalSegments?.length || 'individual'} segments`}
                            style={{ color: '#ff9500' }}
                          >
                            <Scissors />
                          </PartActionButton>
                        )}
                        
                        <PartActionButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportPart(part);
                          }}
                          title="Export this part"
                        >
                          <Download />
                        </PartActionButton>
                        {/* Move part button */}
                        <PartActionButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            showMoveToGroupSelector('part', part, group.id);
                          }}
                          title="Move part"
                          style={{ color: '#007bff' }}
                        >
                          📁
                        </PartActionButton>

                        <PartActionButton 
                          className="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemovePartFromGroup(group.id, part.id);
                          }}
                          title="Remove from group"
                        >
                          <Trash2 />
                        </PartActionButton>
                      </PartActions>
                    </PartHeader>
                    
                    <PartInfo>
                      <PartDuration>
                        Duration: {formatTime(part.endTime - part.startTime)}
                      </PartDuration>
                      <PartTimeRange>
                        {formatTime(part.startTime)} - {formatTime(part.endTime)}
                      </PartTimeRange>
                      {part.speaker && (
                        <span style={{ 
                          fontSize: '10px', 
                          padding: '2px 6px', 
                          backgroundColor: '#007bff20', 
                          borderRadius: '8px',
                          color: '#007bff'
                        }}>
                          {part.speaker}
                        </span>
                      )}

                      {/* Add watermark badge for group parts */}
                      {(part.watermark || part.watermarkSettings || part.watermarks) && (() => {
                        const allWatermarks = new Set();
                        const allWatermarkNames = new Set();
                        
                        if (part.watermarks && part.watermarks.length > 0) {
                          part.watermarks.forEach(w => allWatermarks.add(w));
                        } else if (part.watermark) {
                          allWatermarks.add(part.watermark);
                        }
                        
                        if (part.watermarkNames && part.watermarkNames.length > 0) {
                          part.watermarkNames.forEach(name => allWatermarkNames.add(name));
                        }
                        
                        if (allWatermarks.size > 0) {
                          let displayText = '';
                          
                          if (allWatermarkNames.size > 0) {
                            // Show watermark names (includes both presets and default watermarks)
                            const watermarkList = Array.from(allWatermarkNames);
                            if (watermarkList.length === 1) {
                              displayText = watermarkList[0];
                            } else if (watermarkList.length <= 3) {
                              displayText = watermarkList.join('+');
                            } else {
                              displayText = `${watermarkList.slice(0, 2).join('+')}+${watermarkList.length - 2} more`;
                            }
                          } else if (part.watermarkPresetName) {
                            // Show preset name if available
                            displayText = part.watermarkPresetName;
                          } else {
                            // Fall back to watermark mapping
                            const watermarkMap = {
                              'none': 'None',
                              'text-basic': 'Text',
                              'text-branded': 'Branded',
                              'logo-basic': 'Logo',
                              'logo-branded': 'BLogo'
                            };
                            displayText = Array.from(allWatermarks).map(w => watermarkMap[w] || w).join('+');
                          }
                          
                          return (
                            <LanguageBadge
                              color="#17a2b820"
                              textColor="#17a2b8"
                              borderColor="#17a2b840"
                              style={{ fontSize: '8px', padding: '1px 4px' }}
                            >
                              💧 {displayText}
                            </LanguageBadge>
                          );
                        }
                        return null;
                      })()}
                      
                      {/* Add subtitle badge for individual parts */}
                      {(part.subtitlePreset || part.subtitlePresetName) && (() => {
                        console.log('🎬 Checking subtitle badge for part:', part.id, {
                          subtitlePreset: part.subtitlePreset,
                          subtitlePresetName: part.subtitlePresetName
                        });
                        
                        let displayText = '';
                        
                        if (part.subtitlePresetName && part.subtitlePresetName !== 'None') {
                          displayText = part.subtitlePresetName;
                        }
                        
                        console.log('🎬 Part subtitle badge displayText:', displayText);
                        
                        // Only render if displayText is not empty
                        if (displayText && displayText !== '' && displayText !== 'None') {
                          console.log('🎬 Rendering part subtitle badge:', displayText);
                          return (
                            <LanguageBadge
                              color="#f093fb20"
                              textColor="#f5576c"
                              borderColor="#f093fb40"
                              style={{ fontSize: '8px', padding: '1px 4px' }}
                            >
                              🎬 {displayText}
                            </LanguageBadge>
                          );
                        }
                        console.log('🎬 Not rendering part subtitle badge - no displayText');
                        return null;
                      })()}
                      
                      <span style={{ 
                        fontSize: '8px', 
                        padding: '1px 4px', 
                        backgroundColor: '#007bff20', 
                        borderRadius: '6px',
                        color: '#007bff',
                        fontWeight: '600'
                      }}>
                        PART
                      </span>
                    </PartInfo>
                    
                    {(() => {
                      const displayText = getTranslatedPartText(part, group.language);
                      const isTranslated = group.language && group.language !== 'original';
                      
                      // Always show text section since title no longer contains text
                      return displayText && displayText !== 'Part' && (
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '12px', 
                        color: '#666', 
                        fontStyle: 'italic',
                        maxHeight: '60px',
                        overflow: 'hidden'
                      }}>
                          {/* Always show full text */}
                          <div style={{ marginBottom: isTranslated ? '4px' : '0' }}>
                            "{displayText}"
                          </div>
                          
                          {/* Language indicator for translations */}
                          {isTranslated && (
                            <div>
                              <span style={{
                                fontSize: '10px',
                                padding: '3px 6px',
                                backgroundColor: '#007bff',
                                borderRadius: '8px',
                                color: '#ffffff',
                                fontWeight: '600',
                                border: '1px solid #0056b3'
                              }}>
                                🌍 {getLanguageInfo(group.language).flag} {getLanguageInfo(group.language).name}
                              </span>
                      </div>
                    )}
                        </div>
                      );
                    })()}
                  </PartCard>
                ))}
                
                {/* Empty group state */}
                {(group.segments?.length || 0) === 0 && (group.parts?.length || 0) === 0 && (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#6c757d',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    border: '2px dashed #dee2e6',
                    borderRadius: '8px',
                    background: '#f8f9fa'
                  }}>
                    📂 This group is empty
                    <br />
                    <span style={{ fontSize: '12px', color: '#adb5bd' }}>
                      Drag segments or parts here, or use the move options from other groups
                    </span>
                  </div>
                )}
              </PartsList>
            </GroupContent>
          )}
        </CutterSection>
      ))}



      <CutterSection>
        <SectionTitle>
          <Plus size={14} />
          Create New Part
        </SectionTitle>
        
        <TimeInputs>
          <TimeGroup>
            <TimeLabel>Start Time</TimeLabel>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <TimeInput
                type="text"
                placeholder="0:00"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
              <CurrentTimeButton onClick={() => handleSetCurrentTime('start')}>
                Use Current
              </CurrentTimeButton>
            </div>
          </TimeGroup>

          <TimeGroup>
            <TimeLabel>End Time</TimeLabel>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <TimeInput
                type="text"
                placeholder="0:00"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
              <CurrentTimeButton onClick={() => handleSetCurrentTime('end')}>
                Use Current
              </CurrentTimeButton>
            </div>
          </TimeGroup>
        </TimeInputs>

        <CreatePartForm>
          <PartNameInput
            type="text"
            placeholder="Part name (e.g., Introduction, Main Topic, Conclusion)"
            value={partName}
            onChange={(e) => setPartName(e.target.value)}
          />
          <ActionButton variant="primary" onClick={handleCreatePart}>
            <Plus />
            Create Part
          </ActionButton>
        </CreatePartForm>
      </CutterSection>

      <PartsContainer>
        {individualParts.length === 0 && groups.length === 0 && (!project.exportedParts || project.exportedParts.length === 0) ? (
          <EmptyState>
            <EmptyIcon>
              <Scissors size={24} color="#6c757d" />
            </EmptyIcon>
            <EmptyTitle>No Parts or Groups Created</EmptyTitle>
            <EmptyDescription>
              Create parts manually using the form below, add segments from transcript, or create groups to organize your content. 
              Each part can be exported as a separate video file.
            </EmptyDescription>
          </EmptyState>
        ) : null}

        {/* Exported Parts Section - Display as a collapsible group */}
        {project.exportedParts && project.exportedParts.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <CollapsibleGroupTitle 
              onClick={() => setExpandedGroups(prev => ({
                ...prev,
                'exported-parts': !prev['exported-parts']
              }))}
              style={{ cursor: 'pointer' }}
            >
              <GroupTitleLeft>
                <span style={{ fontWeight: '600', fontSize: '14px' }}>📦 Exported Parts</span>
                <span style={{ fontSize: '12px', color: '#6c757d', marginLeft: '8px' }}>
                  ({project.exportedParts.length})
                </span>
              </GroupTitleLeft>
              <ExpandIcon>
                {expandedGroups['exported-parts'] !== false ? <ChevronDown /> : <ChevronUp />}
              </ExpandIcon>
            </CollapsibleGroupTitle>
            
            {expandedGroups['exported-parts'] !== false && (
              <GroupContent>
                <PartsList>
                  {project.exportedParts.map((exportedPart) => (
                    <PartCard 
                      key={exportedPart.id}
                      style={{ 
                        borderLeft: '3px solid #28a745',
                        marginBottom: '8px'
                      }}
                    >
                      <PartHeader>
                        <PartName>
                          📦 {exportedPart.name}
                        </PartName>
                        <PartActions>
                          {exportedPart.videoUrl ? (
                            <PartActionButton
                              onClick={(e) => {
                                e.stopPropagation();
                                const link = document.createElement('a');
                                link.href = exportedPart.videoUrl;
                                link.download = `${exportedPart.name}.mp4`;
                                link.click();
                              }}
                              title="Download exported video"
                            >
                              <Download />
                            </PartActionButton>
                          ) : (
                            <PartActionButton
                              title="Video extraction pending (requires Desktop Service or Electron)"
                              style={{ color: '#ffc107' }}
                            >
                              ⏳
                            </PartActionButton>
                          )}
                          <PartActionButton
                            className="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete exported part "${exportedPart.name}"?`)) {
                                const updatedExportedParts = project.exportedParts.filter(
                                  ep => ep.id !== exportedPart.id
                                );
                                onUpdate({ exportedParts: updatedExportedParts });
                              }
                            }}
                            title="Delete exported part"
                          >
                            <Trash2 />
                          </PartActionButton>
                        </PartActions>
                      </PartHeader>
                      
                      <PartInfo>
                        <PartDuration>
                          Duration: {formatTime(exportedPart.duration)}
                        </PartDuration>
                        <PartTimeRange>
                          {formatTime(exportedPart.startTime)} - {formatTime(exportedPart.endTime)}
                        </PartTimeRange>
                        <div style={{ fontSize: '11px', color: '#868e96', marginTop: '4px' }}>
                          Exported: {new Date(exportedPart.exportedAt).toLocaleString()}
                        </div>
                      </PartInfo>
                    </PartCard>
                  ))}
                </PartsList>
              </GroupContent>
            )}
          </div>
        )}
      </PartsContainer>
      {/* 🟢 NEW: Export Status Modal */}
      {exportStatus && (
        <StatusModalOverlay>
          <StatusModalContent>
            <StatusIconWrapper className={exportStatus.type}>
              {exportStatus.type === 'loading' && <Loader2 className="animate-spin" size={32} />}
              {exportStatus.type === 'success' && <CheckCircle size={32} />}
              {exportStatus.type === 'error' && <AlertCircle size={32} />}
            </StatusIconWrapper>
            
            <StatusTitle>
              {exportStatus.type === 'loading' && 'Exporting Video...'}
              {exportStatus.type === 'success' && 'Export Complete!'}
              {exportStatus.type === 'error' && 'Export Error'}
            </StatusTitle>
            
            <StatusMessage>{exportStatus.message}</StatusMessage>
            
            {exportStatus.type === 'loading' && (
              <ProgressBarContainer>
                <ProgressBarFill progress={exportStatus.progress || 0} />
              </ProgressBarContainer>
            )}
            
            <StatusActions>
              {exportStatus.type === 'success' && exportStatus.folder && (
                <StatusButton 
                  className="primary" 
                  onClick={() => handleOpenFolder(exportStatus.folder)}
                >
                  <ExternalLink size={16} /> Open Folder
                </StatusButton>
              )}
              
              {(exportStatus.type === 'success' || exportStatus.type === 'error') && (
                <StatusButton 
                  className="secondary" 
                  onClick={() => setExportStatus(null)}
                >
                  Close
                </StatusButton>
              )}
            </StatusActions>
          </StatusModalContent>
        </StatusModalOverlay>
      )}
    </Container>
  );
};

export default PartsCutter;